import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Package, Image as ImageIcon, KeyRound, Plus, Pencil, Trash2, Upload, X, Loader2, Flame, ExternalLink, ListOrdered, Tag, Salad, Eye, EyeOff, Printer, Bell, MessageCircle, Wallet, Globe, Boxes, Palette, Building2, Megaphone, Download, Archive, Truck, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BRAND_DEFAULTS, FUENTES, RADIOS, brandingVars } from "@/context/ThemeContext";
import { API, BACKEND_URL, formatARS } from "@/lib/format";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const CATS = [
  { key: "piedra", label: "Pizzas Piedra" },
  { key: "molde", label: "Pizzas Molde" },
  { key: "xl", label: "Pizzas XL" },
  { key: "empanadas", label: "Empanadas" },
  { key: "bebidas", label: "Bebidas" },
  { key: "dulces", label: "Dulces" },
  { key: "promos", label: "Promos" },
];

const INTRO_DEFAULTS = {
  paso1Titulo: "¿Qué querés comer hoy?",
  paso2Titulo: "¿A la piedra o al molde?",
  pizzas: { titulo: "Pizzas", subtitulo: "Piedra · Molde", imagen: "" },
  empanadas: { titulo: "Empanadas", subtitulo: "12 sabores · docena promo", imagen: "" },
  piedra: { titulo: "A la Piedra", subtitulo: "Masa fina y crocante", imagen: "" },
  molde: { titulo: "Al Molde", subtitulo: "Masa esponjosa y alta", imagen: "" },
};

const INTRO_CARDS = [
  { key: "pizzas", label: "Tarjeta Pizzas (paso 1)" },
  { key: "empanadas", label: "Tarjeta Empanadas (paso 1)" },
  { key: "piedra", label: "Tarjeta A la Piedra (paso 2)" },
  { key: "molde", label: "Tarjeta Al Molde (paso 2)" },
];

const resolveImg = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

export default function AdminPanel() {
  const { authHeader, logout, me } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(null);

  const isSuper = me?.platform_role === "superadmin";
  const esOperador = !isSuper && me?.memberships?.[0]?.tenant_role === "operador_local";
  const tabEfectivo = esOperador ? (["orders", "stock"].includes(tab) ? tab : "orders") : (tab || "products");

  // El switcher de empresa solo persiste en localStorage: al recargar /admin hay que
  // re-aplicar el header X-Tenant (App.js ya no lo hace, para no contaminar la tienda).
  // Para no-superadmins se usa su membership; así el aviso "estás editando" siempre es correcto.
  const [tenantEditando, setTenantEditando] = useState("");
  useEffect(() => {
    if (!me) return;
    const t = localStorage.getItem("bp_admin_tenant") || me?.memberships?.[0]?.tenant_id || "bigpizza";
    axios.defaults.headers.common["X-Tenant"] = t;
    setTenantEditando(t);
    axios.get(`${API}/config`)
      .then((r) => setTenantEditando(r.data?.tenant?.nombre || t))
      .catch(() => {});
  }, [me]);

  return (
    <div className="min-h-screen bg-ink">
      <header className="sticky top-0 z-40 bg-ink/95 backdrop-blur-xl border-b border-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-brand flex items-center justify-center rotate-[-6deg]">
              <Flame className="w-5 h-5 text-black" strokeWidth={3} />
            </div>
            <div className="font-heading text-2xl text-white"><span className="text-xs text-zinc-500 uppercase tracking-widest">Panel Admin</span></div>
          </a>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-xs text-zinc-500 uppercase tracking-widest">{me?.email}</span>
            {isSuper && <TenantSwitcher authHeader={authHeader} />}
            <a href="/" className="text-xs text-zinc-400 hover:text-white uppercase tracking-widest flex items-center gap-1">
              <ExternalLink className="w-3.5 h-3.5" /> Tienda
            </a>
            <button
              data-testid="admin-logout-button"
              onClick={() => { logout(); navigate("/admin/login"); }}
              className="flex items-center gap-2 bg-panel hover:bg-red-500/20 border border-line hover:border-red-500 text-white text-xs font-bold uppercase tracking-widest px-3 py-2 rounded transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Salir
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-2 mb-6 overflow-x-auto thin-scroll pb-3 -mx-4 px-4 sm:mx-0 sm:px-0">
          {isSuper && <TabBtn active={tabEfectivo === "platform"} onClick={() => setTab("platform")} icon={<Globe className="w-4 h-4" />} label="Plataforma" testid="tab-platform" />}
          {!esOperador && (
            <>
              <TabBtn active={tabEfectivo === "products"} onClick={() => setTab("products")} icon={<Package className="w-4 h-4" />} label="Productos" testid="tab-products" />
              <TabBtn active={tabEfectivo === "promos"} onClick={() => setTab("promos")} icon={<Tag className="w-4 h-4" />} label="Promos" testid="tab-promos" />
              <TabBtn active={tabEfectivo === "extras"} onClick={() => setTab("extras")} icon={<Salad className="w-4 h-4" />} label="Extras" testid="tab-extras" />
              <TabBtn active={tabEfectivo === "payments"} onClick={() => setTab("payments")} icon={<Wallet className="w-4 h-4" />} label="Pagos" testid="tab-payments" />
              <TabBtn active={tabEfectivo === "envios"} onClick={() => setTab("envios")} icon={<Truck className="w-4 h-4" />} label="Envíos" testid="tab-envios" />
              <TabBtn active={tabEfectivo === "settings"} onClick={() => setTab("settings")} icon={<ImageIcon className="w-4 h-4" />} label="Logo & Marca" testid="tab-settings" />
              <TabBtn active={tabEfectivo === "integraciones"} onClick={() => setTab("integraciones")} icon={<Megaphone className="w-4 h-4" />} label="Integraciones" testid="tab-integraciones" />
            </>
          )}
          {esOperador && <TabBtn active={tabEfectivo === "stock"} onClick={() => setTab("stock")} icon={<Boxes className="w-4 h-4" />} label="Stock" testid="tab-stock" />}
          <TabBtn active={tabEfectivo === "orders"} onClick={() => setTab("orders")} icon={<ListOrdered className="w-4 h-4" />} label="Pedidos" testid="tab-orders" />
          <TabBtn active={tabEfectivo === "password"} onClick={() => setTab("password")} icon={<KeyRound className="w-4 h-4" />} label="Contraseña" testid="tab-password" />
        </div>

        {tenantEditando && (
          <div
            data-testid="admin-editing-tenant-banner"
            className="mb-6 flex items-center gap-2 bg-gold/10 border border-gold/40 rounded-lg px-4 py-2.5"
          >
            <Building2 className="w-4 h-4 text-gold shrink-0" />
            <span className="text-xs sm:text-sm text-zinc-300 uppercase tracking-widest">
              Estás editando: <strong className="text-gold font-black">{tenantEditando}</strong>
            </span>
            {isSuper && (
              <span className="ml-auto hidden sm:inline text-[10px] text-zinc-500 uppercase tracking-widest">
                Cambiá de empresa con el selector de arriba
              </span>
            )}
          </div>
        )}

        {tabEfectivo === "platform" && isSuper && <SuperAdminPanel authHeader={authHeader} />}
        {tabEfectivo === "integraciones" && !esOperador && <IntegracionesPanel authHeader={authHeader} />}
        {tabEfectivo === "products" && !esOperador && <ProductsManager authHeader={authHeader} />}
        {tabEfectivo === "promos" && !esOperador && <PromosManager authHeader={authHeader} />}
        {tabEfectivo === "extras" && !esOperador && <ExtrasManager authHeader={authHeader} />}
        {tabEfectivo === "payments" && !esOperador && <PaymentMethodsManager authHeader={authHeader} />}
        {tabEfectivo === "envios" && !esOperador && <EnviosPanel authHeader={authHeader} />}
        {tabEfectivo === "settings" && !esOperador && <SettingsPanel authHeader={authHeader} />}
        {tabEfectivo === "stock" && esOperador && <StockManager authHeader={authHeader} />}
        {tabEfectivo === "orders" && <OrdersPanel authHeader={authHeader} />}
        {tabEfectivo === "password" && <PasswordPanel authHeader={authHeader} />}
      </div>
    </div>
  );
}


// ============ PLATAFORMA (SUPER-ADMIN) ============
function TenantSwitcher({ authHeader }) {
  const [tenants, setTenants] = useState([]);
  const [current, setCurrent] = useState(() => localStorage.getItem("bp_admin_tenant") || "bigpizza");
  useEffect(() => {
    axios.get(`${API}/superadmin/tenants`, { headers: authHeader() }).then((r) => setTenants(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const cambiar = (v) => {
    localStorage.setItem("bp_admin_tenant", v);
    axios.defaults.headers.common["X-Tenant"] = v;
    window.location.reload();
  };
  return (
    <select
      data-testid="tenant-switcher"
      value={current}
      onChange={(e) => cambiar(e.target.value)}
      className="bg-panel border border-gold/50 text-gold text-xs font-bold uppercase tracking-widest rounded px-2 py-1.5 outline-none cursor-pointer"
    >
      {tenants.map((t) => (
        <option key={t.id} value={t.id}>{t.nombre}</option>
      ))}
    </select>
  );
}

function StockManager({ authHeader }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/products`, { headers: authHeader() });
      setProducts(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (p) => {
    try {
      await axios.put(`${API}/admin/products/${p.id}`, { ...p, sinStock: !p.sinStock }, { headers: authHeader() });
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, sinStock: !x.sinStock } : x)));
      toast.success(p.sinStock ? `${p.nombre} disponible` : `${p.nombre} sin stock`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar el stock");
    }
  };

  if (loading) return <div className="text-zinc-500 py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando...</div>;

  return (
    <div className="max-w-2xl">
      <h2 className="font-heading text-3xl uppercase text-white mb-1">Stock del local</h2>
      <p className="text-sm text-zinc-500 mb-4">Tocá un producto para marcarlo sin stock / disponible</p>
      <div data-testid="stock-list" className="space-y-2">
        {products.map((p) => (
          <div key={p.id} data-testid={`stock-row-${p.id}`} className="bg-panel border border-line rounded-lg p-3 flex items-center justify-between gap-3">
            <span className="text-sm font-bold text-white truncate">{p.nombre}</span>
            <button
              data-testid={`stock-toggle-${p.id}`}
              onClick={() => toggle(p)}
              className={`shrink-0 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded transition-colors ${p.sinStock ? "bg-red-500/20 text-red-400 border border-red-500/50" : "bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/40"}`}
            >
              {p.sinStock ? "Sin stock" : "Disponible"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SuperAdminPanel({ authHeader }) {
  const [tenants, setTenants] = useState(null);
  const [sel, setSel] = useState(null);
  const [locales, setLocales] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(null);

  const loadTenants = async () => {
    const { data } = await axios.get(`${API}/superadmin/tenants`, { headers: authHeader() });
    setTenants(data);
    setSel((s) => s || data[0]?.id || null);
  };
  const loadDetalle = async (tid) => {
    if (!tid) return;
    const [l, u] = await Promise.all([
      axios.get(`${API}/superadmin/locales?tenant_id=${tid}`, { headers: authHeader() }),
      axios.get(`${API}/superadmin/users?tenant_id=${tid}`, { headers: authHeader() }),
    ]);
    setLocales(l.data);
    setUsers(u.data);
  };
  useEffect(() => { loadTenants().catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadDetalle(sel).catch(() => {}); }, [sel]); // eslint-disable-line react-hooks/exhaustive-deps
  const reload = () => { loadTenants(); loadDetalle(sel); };

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-3xl uppercase text-white">Plataforma</h2>
          <p className="text-sm text-zinc-500">Empresas, locales y usuarios del sistema</p>
        </div>
        <button
          data-testid="sa-nueva-empresa"
          onClick={() => setForm("onboard")}
          className="flex items-center gap-2 bg-brand hover:bg-brand-strong text-black font-black uppercase text-xs px-4 py-2 rounded transition-colors"
        >
          <Plus className="w-4 h-4" strokeWidth={3} /> Nueva empresa
        </button>
      </div>

      {form === "onboard" && <OnboardForm authHeader={authHeader} tenants={tenants || []} onClose={() => setForm(null)} onSaved={reload} />}
      {form === "local" && <LocalForm authHeader={authHeader} tenantId={sel} onClose={() => setForm(null)} onSaved={reload} />}
      {form === "user" && <UserForm authHeader={authHeader} tenantId={sel} locales={locales} onClose={() => setForm(null)} onSaved={reload} />}

      {tenants === null && (
        <div className="text-zinc-500 text-sm py-4 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando empresas...</div>
      )}
      <div className="flex gap-2 flex-wrap">
        {(tenants || []).map((t) => (
          <button
            key={t.id}
            data-testid={`sa-tenant-${t.id}`}
            onClick={() => setSel(t.id)}
            className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest font-bold border transition-colors ${sel === t.id ? "bg-brand text-black border-brand" : "bg-panel text-zinc-300 border-line hover:border-white/40"}`}
          >
            {t.nombre}{!t.activo ? " (inactiva)" : ""}
          </button>
        ))}
      </div>

      {sel && (
        <>
          <div className="bg-panel border border-line rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-xl uppercase text-white">Locales</h3>
              <button data-testid="sa-agregar-local" onClick={() => setForm("local")} className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-gold hover:text-white transition-colors">
                <Plus className="w-3.5 h-3.5" /> Agregar local
              </button>
            </div>
            <div data-testid="sa-locales-list" className="space-y-2">
              {locales.map((l) => (
                <div key={l.id} data-testid={`sa-local-${l.id}`} className="flex items-center justify-between bg-black border border-line rounded px-3 py-2 gap-2">
                  <span className="text-sm text-white font-bold">{l.nombre}</span>
                  <span className="text-xs text-zinc-500 truncate">{l.direccion || "—"}</span>
                </div>
              ))}
              {locales.length === 0 && <div className="text-xs text-zinc-500">Sin locales</div>}
            </div>
          </div>

          <div className="bg-panel border border-line rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-xl uppercase text-white">Usuarios</h3>
              <button data-testid="sa-agregar-usuario" onClick={() => setForm("user")} className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-gold hover:text-white transition-colors">
                <Plus className="w-3.5 h-3.5" /> Agregar usuario
              </button>
            </div>
            <div data-testid="sa-users-list" className="space-y-2">
              {users.map((u) => {
                const m = u.memberships?.[0];
                const rolLabel = u.platform_role === "superadmin" ? "Super-admin" : m?.tenant_role === "admin_empresa" ? "Admin empresa" : `Operador · ${locales.find((l) => l.id === m?.local_id)?.nombre || "local"}`;
                return (
                  <div key={u.id} data-testid={`sa-user-${u.id}`} className="flex items-center justify-between bg-black border border-line rounded px-3 py-2 gap-2">
                    <div className="min-w-0">
                      <span className="text-sm text-white font-bold">{u.nombre}</span>
                      <span className="text-xs text-zinc-500 ml-2">{u.email}</span>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest font-black text-gold shrink-0">{rolLabel}</span>
                  </div>
                );
              })}
              {users.length === 0 && <div className="text-xs text-zinc-500">Sin usuarios</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function OnboardForm({ authHeader, onClose, onSaved, tenants = [] }) {
  const [f, setF] = useState({ empresa_nombre: "", empresa_slug: "", local_nombre: "", local_direccion: "", admin_nombre: "", admin_email: "", admin_password: "", copiar: false, copiarDe: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/superadmin/onboard`, {
        empresa_nombre: f.empresa_nombre,
        empresa_slug: f.empresa_slug,
        local_nombre: f.local_nombre,
        local_direccion: f.local_direccion,
        admin_nombre: f.admin_nombre,
        admin_email: f.admin_email,
        admin_password: f.admin_password,
        copiarContenidoDe: f.copiar && f.copiarDe ? f.copiarDe : null,
      }, { headers: authHeader() });
      toast.success(`Empresa "${f.empresa_nombre}" creada con su local y su admin`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear la empresa");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="sa-onboard-form" className="bg-[#0F0F11] border border-brand/40 rounded-xl p-5 space-y-3">
      <h3 className="font-heading text-xl uppercase text-white">Nueva empresa — alta en un paso</h3>
      <p className="text-xs text-zinc-500">Crea la empresa, su primer local y el usuario administrador. Sin conocimiento técnico.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <FormField label="Nombre de la empresa" testid="onboard-empresa" value={f.empresa_nombre} onChange={(v) => setF({ ...f, empresa_nombre: v })} placeholder="Ej: Pizzería Don Pepe" />
        <FormField label="Slug (subdominio)" testid="onboard-slug" value={f.empresa_slug} onChange={(v) => setF({ ...f, empresa_slug: v })} placeholder="donpepe" />
        <FormField label="Local principal" testid="onboard-local" value={f.local_nombre} onChange={(v) => setF({ ...f, local_nombre: v })} placeholder="Casa Central" />
        <FormField label="Dirección del local" testid="onboard-direccion" value={f.local_direccion} onChange={(v) => setF({ ...f, local_direccion: v })} placeholder="Av. Ejemplo 123" />
        <FormField label="Nombre del admin" testid="onboard-admin-nombre" value={f.admin_nombre} onChange={(v) => setF({ ...f, admin_nombre: v })} />
        <FormField label="Email del admin" testid="onboard-admin-email" type="email" autoComplete="off" value={f.admin_email} onChange={(v) => setF({ ...f, admin_email: v })} />
        <FormField label="Contraseña del admin" testid="onboard-admin-pass" type="password" autoComplete="new-password" value={f.admin_password} onChange={(v) => setF({ ...f, admin_password: v })} />
      </div>
      <div className="space-y-2">
        <ToggleChk label="Copiar menú, promos y medios de pago de otra empresa (arranca con contenido listo para editar)" checked={f.copiar} onChange={(v) => setF({ ...f, copiar: v })} testid="onboard-copiar" />
        {f.copiar && (
          <select
            data-testid="onboard-copiar-de"
            value={f.copiarDe}
            onChange={(e) => setF({ ...f, copiarDe: e.target.value })}
            className="w-full bg-black border border-line focus:border-brand rounded px-3 py-2.5 text-sm text-white outline-none"
          >
            <option value="">Elegí la empresa origen...</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.nombre}</option>
            ))}
          </select>
        )}
        {f.copiar && (
          <p className="text-[11px] text-zinc-500">No se copian datos de cobro (alias/CBU), WhatsApp ni Google Ads: la empresa nueva configura los suyos.</p>
        )}
      </div>
      <div className="flex gap-2 pt-2">
        <button onClick={onClose} className="flex-1 bg-panel border border-line hover:border-white text-white font-bold uppercase text-sm py-3 rounded transition-colors">Cancelar</button>
        <button
          data-testid="onboard-save"
          onClick={save}
          disabled={saving || !f.empresa_nombre || !f.empresa_slug || !f.local_nombre || !f.admin_email || !f.admin_password || (f.copiar && !f.copiarDe)}
          className="flex-1 bg-brand hover:bg-brand-strong disabled:bg-zinc-700 text-black font-black uppercase text-sm py-3 rounded transition-colors"
        >
          {saving ? "Creando..." : "Crear empresa"}
        </button>
      </div>
    </div>
  );
}

function LocalForm({ authHeader, tenantId, onClose, onSaved }) {
  const [nombre, setNombre] = useState("");
  const [direccion, setDireccion] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/superadmin/locales`, { tenant_id: tenantId, nombre, direccion }, { headers: authHeader() });
      toast.success("Local creado");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear el local");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="sa-local-form" className="bg-[#0F0F11] border border-gold/40 rounded-xl p-5 space-y-3">
      <h3 className="font-heading text-xl uppercase text-white">Nuevo local</h3>
      <FormField label="Nombre del local" testid="local-nombre" value={nombre} onChange={setNombre} placeholder="Sucursal Norte" />
      <FormField label="Dirección" testid="local-direccion" value={direccion} onChange={setDireccion} placeholder="Av. Ejemplo 456" />
      <div className="flex gap-2 pt-1">
        <button onClick={onClose} className="flex-1 bg-panel border border-line hover:border-white text-white font-bold uppercase text-sm py-2.5 rounded transition-colors">Cancelar</button>
        <button data-testid="local-save" onClick={save} disabled={saving || !nombre} className="flex-1 bg-brand hover:bg-brand-strong disabled:bg-zinc-700 text-black font-black uppercase text-sm py-2.5 rounded transition-colors">
          {saving ? "Guardando..." : "Crear local"}
        </button>
      </div>
    </div>
  );
}

function UserForm({ authHeader, tenantId, locales, onClose, onSaved }) {
  const [f, setF] = useState({ nombre: "", email: "", password: "", rol: "operador_local", local_id: locales[0]?.id || "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/superadmin/users`, {
        email: f.email,
        nombre: f.nombre,
        password: f.password,
        tenant_id: tenantId,
        tenant_role: f.rol,
        local_id: f.rol === "operador_local" ? f.local_id : null,
      }, { headers: authHeader() });
      toast.success("Usuario creado");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear el usuario");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="sa-user-form" className="bg-[#0F0F11] border border-gold/40 rounded-xl p-5 space-y-3">
      <h3 className="font-heading text-xl uppercase text-white">Nuevo usuario</h3>
      <FormField label="Nombre" testid="user-nombre" value={f.nombre} onChange={(v) => setF({ ...f, nombre: v })} />
      <FormField label="Email" testid="user-email" type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} />
      <FormField label="Contraseña" testid="user-password" type="password" value={f.password} onChange={(v) => setF({ ...f, password: v })} />
      <div>
        <span className="block text-[11px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Rol</span>
        <div className="flex gap-2">
          {[["admin_empresa", "Admin empresa"], ["operador_local", "Operador de local"]].map(([key, label]) => (
            <button
              key={key}
              data-testid={`user-rol-${key}`}
              onClick={() => setF({ ...f, rol: key })}
              className={`flex-1 py-2 rounded border font-bold uppercase text-[11px] tracking-widest transition-colors ${f.rol === key ? "border-brand bg-brand/10 text-white" : "border-line text-zinc-500 hover:border-white/30"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {f.rol === "operador_local" && (
        <div>
          <span className="block text-[11px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Local asignado</span>
          <select
            data-testid="user-local"
            value={f.local_id}
            onChange={(e) => setF({ ...f, local_id: e.target.value })}
            className="w-full bg-black border border-line focus:border-brand rounded px-3 py-2.5 text-sm text-white outline-none"
          >
            {locales.map((l) => (
              <option key={l.id} value={l.id}>{l.nombre}</option>
            ))}
          </select>
        </div>
      )}
      <div className="flex gap-2 pt-1">
        <button onClick={onClose} className="flex-1 bg-panel border border-line hover:border-white text-white font-bold uppercase text-sm py-2.5 rounded transition-colors">Cancelar</button>
        <button
          data-testid="user-save"
          onClick={save}
          disabled={saving || !f.nombre || !f.email || !f.password || (f.rol === "operador_local" && !f.local_id)}
          className="flex-1 bg-brand hover:bg-brand-strong disabled:bg-zinc-700 text-black font-black uppercase text-sm py-2.5 rounded transition-colors"
        >
          {saving ? "Guardando..." : "Crear usuario"}
        </button>
      </div>
    </div>
  );
}

function IntegracionesPanel({ authHeader }) {
  const [gads, setGads] = useState({ id: "", conversionLabel: "", activo: false });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/config`)
      .then((r) => {
        setGads({ id: "", conversionLabel: "", activo: false, ...(r.data?.googleAds || {}) });
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    try {
      await axios.post(`${API}/admin/config`, { googleAds: gads }, { headers: authHeader() });
      toast.success("Integración de Google Ads guardada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    }
  };

  if (!loaded) return <div className="text-zinc-500"><Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Cargando...</div>;

  return (
    <div>
      <h3 className="font-heading text-2xl uppercase text-white mb-1 flex items-center gap-2"><Megaphone className="w-5 h-5 text-brand" /> Google Ads</h3>
      <p className="text-xs text-zinc-500 mb-4">Etiqueta de conversión de compra. Si está activa, la tienda carga gtag.js y dispara la conversión al confirmarse cada pedido (una sola vez por pedido; los pedidos de prueba no cuentan).</p>
      <div className="bg-panel border border-line rounded-xl p-5 space-y-4 max-w-xl">
        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">ID de Google Ads (formato AW-XXXXXXXXX)</span>
          <input
            data-testid="int-gads-id"
            value={gads.id} onChange={(e) => setGads({ ...gads, id: e.target.value.toUpperCase() })}
            placeholder="AW-123456789"
            className="mt-1 w-full bg-black border border-line focus:border-brand rounded px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none font-mono"
          />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Etiqueta de conversión de compra</span>
          <input
            data-testid="int-gads-label"
            value={gads.conversionLabel} onChange={(e) => setGads({ ...gads, conversionLabel: e.target.value })}
            placeholder="Ej: abCDefGhIJK"
            className="mt-1 w-full bg-black border border-line focus:border-brand rounded px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none font-mono"
          />
        </label>
        <button
          data-testid="int-gads-activo"
          onClick={() => setGads({ ...gads, activo: !gads.activo })}
          className={`flex items-center gap-2 px-4 py-2.5 rounded text-xs font-black uppercase tracking-widest transition-colors ${gads.activo ? "bg-emerald-500 text-black" : "bg-black border border-line text-zinc-400"}`}
        >
          {gads.activo ? "Activada" : "Desactivada"}
        </button>
        <button
          data-testid="admin-save-integraciones"
          onClick={save}
          className="w-full bg-brand hover:bg-brand-strong text-black font-black uppercase text-sm py-3 rounded transition-colors"
        >Guardar integración</button>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon, label, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-widest font-bold transition-colors border ${
        active ? "bg-brand text-black border-brand" : "bg-panel text-zinc-300 border-line hover:border-white/40"
      }`}
    >
      {icon} {label}
    </button>
  );
}

// ============ PRODUCTS ============
function ProductsManager({ authHeader }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // product or "new"

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/products`, { headers: authHeader() });
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleStock = async (p) => {
    const payload = {
      nombre: p.nombre,
      descripcion: p.descripcion || "",
      categoria: p.categoria,
      esPromo: !!p.esPromo,
      esCombo: !!p.esCombo,
      etiqueta: p.etiqueta || null,
      esSugerido: !!p.esSugerido,
      orden: p.orden ?? 0,
      precio: p.precio ?? null,
      precioGrande: p.precioGrande ?? null,
      precioChica: p.precioChica ?? null,
      imagen: p.imagen || null,
      tags: p.tags || [],
      activo: p.activo !== false,
      sinStock: !p.sinStock,
    };
    await axios.put(`${API}/admin/products/${p.id}`, payload, { headers: authHeader() });
    toast.success(p.sinStock ? "De vuelta en stock" : "Sin stock — ya no se muestra en la tienda");
    load();
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar producto?")) return;
    await axios.delete(`${API}/admin/products/${id}`, { headers: authHeader() });
    toast.success("Producto eliminado");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-3xl uppercase text-white">Productos</h2>
          <p className="text-sm text-zinc-500">{items.length} en el catálogo</p>
        </div>
        <button
          data-testid="admin-add-product-button"
          onClick={() => setEditing("new")}
          className="flex items-center gap-2 bg-brand hover:bg-brand-strong text-black font-black uppercase text-xs px-4 py-2 rounded"
        >
          <Plus className="w-4 h-4" strokeWidth={3} /> Nuevo
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando...</div>
      ) : (
        <div data-testid="admin-products-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((p) => (
            <div key={p.id} data-testid={`admin-product-row-${p.id}`} className="flex gap-3 bg-panel border border-line rounded-lg p-3">
              <div className="w-16 h-16 rounded overflow-hidden bg-black shrink-0">
                {p.imagen && <img src={resolveImg(p.imagen)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-white line-clamp-1">{p.nombre}</div>
                <div className="text-xs text-zinc-500 line-clamp-1">{CATS.find((c) => c.key === p.categoria)?.label || p.categoria}</div>
                <div className="mt-1 flex gap-1 items-center flex-wrap">
                  {p.precio && <span className="text-gold font-bold text-xs">{formatARS(p.precio)}</span>}
                  {p.precioGrande && <span className="text-gold font-bold text-xs">G {formatARS(p.precioGrande)} · C {formatARS(p.precioChica)}</span>}
                  {p.esPromo && <span className="bg-brand text-black text-[9px] px-1.5 py-0.5 rounded font-black uppercase">Promo</span>}
                  {p.sinStock && <span className="bg-red-600 text-white text-[9px] px-1.5 py-0.5 rounded font-black uppercase">Sin stock</span>}
                  {p.esCombo && <span className="bg-gold text-black text-[9px] px-1.5 py-0.5 rounded font-black uppercase">Combo</span>}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <button
                  data-testid={`admin-stock-${p.id}`}
                  onClick={() => toggleStock(p)}
                  title={p.sinStock ? "Volver a poner en stock" : "Marcar sin stock"}
                  className={`w-8 h-8 flex items-center justify-center bg-black border rounded transition-colors ${p.sinStock ? "border-red-500 text-red-400 hover:border-white hover:text-white" : "border-line text-zinc-300 hover:border-red-500 hover:text-red-400"}`}
                >
                  {p.sinStock ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                </button>
                <button data-testid={`admin-edit-${p.id}`} onClick={() => setEditing(p)} className="w-8 h-8 flex items-center justify-center bg-black border border-line hover:border-white rounded text-zinc-300 hover:text-white transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                <button data-testid={`admin-delete-${p.id}`} onClick={() => del(p.id)} className="w-8 h-8 flex items-center justify-center bg-black border border-line hover:border-red-500 rounded text-zinc-300 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <ProductForm authHeader={authHeader} product={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ProductForm({ authHeader, product, onClose, onSaved }) {
  const [form, setForm] = useState({
    nombre: product?.nombre || "",
    descripcion: product?.descripcion || "",
    categoria: product?.categoria || "piedra",
    precio: product?.precio || "",
    precioGrande: product?.precioGrande || "",
    precioChica: product?.precioChica || "",
    imagen: product?.imagen || "",
    esPromo: product?.esPromo || false,
    esCombo: product?.esCombo || false,
    etiqueta: product?.etiqueta || "",
    esSugerido: product?.esSugerido || false,
    orden: product?.orden ?? 0,
    sinStock: product?.sinStock || false,
    activo: product?.activo !== false,
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      setForm((s) => ({ ...s, imagen: data.url }));
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir");
    } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        precio: form.precio === "" ? null : Number(form.precio),
        precioGrande: form.precioGrande === "" ? null : Number(form.precioGrande),
        precioChica: form.precioChica === "" ? null : Number(form.precioChica),
      };
      if (product) {
        await axios.put(`${API}/admin/products/${product.id}`, payload, { headers: authHeader() });
        toast.success("Producto actualizado");
      } else {
        await axios.post(`${API}/admin/products`, payload, { headers: authHeader() });
        toast.success("Producto creado");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const isPizza = ["piedra", "molde"].includes(form.categoria);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="admin-product-form" className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto thin-scroll bg-[#0F0F11] border border-line rounded-xl p-6 slide-up">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-2xl uppercase text-white">{product ? "Editar producto" : "Nuevo producto"}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <FormField label="Nombre" testid="pf-nombre" value={form.nombre} onChange={(v) => setForm({ ...form, nombre: v })} />
          <FormSelect label="Categoría" testid="pf-categoria" value={form.categoria} onChange={(v) => setForm({ ...form, categoria: v })} options={CATS} />
          <FormField label="Descripción" testid="pf-desc" value={form.descripcion} onChange={(v) => setForm({ ...form, descripcion: v })} as="textarea" wrap />
          {isPizza ? (
            <>
              <FormField label="Precio Grande" testid="pf-precio-grande" value={form.precioGrande} onChange={(v) => setForm({ ...form, precioGrande: v })} type="number" />
              <FormField label="Precio Chica" testid="pf-precio-chica" value={form.precioChica} onChange={(v) => setForm({ ...form, precioChica: v })} type="number" />
            </>
          ) : (
            <FormField label="Precio" testid="pf-precio" value={form.precio} onChange={(v) => setForm({ ...form, precio: v })} type="number" />
          )}
        </div>

        <div className="mt-4">
          <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Imagen</span>
          <div className="mt-2 flex items-center gap-3">
            <div className="w-24 h-24 rounded bg-black border border-line overflow-hidden">
              {form.imagen && <img src={resolveImg(form.imagen)} alt="" className="w-full h-full object-cover" />}
            </div>
            <button
              data-testid="pf-upload-button"
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 bg-panel border border-line hover:border-white text-white text-xs uppercase tracking-widest font-bold px-3 py-2 rounded"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Subir foto
            </button>
            <input ref={fileRef} data-testid="pf-file-input" type="file" accept="image/*" className="hidden" onChange={upload} />
            <input
              data-testid="pf-image-url"
              type="text" value={form.imagen} onChange={(e) => setForm({ ...form, imagen: e.target.value })} placeholder="o pegá una URL"
              className="flex-1 bg-black border border-line focus:border-brand rounded px-3 py-2 text-xs text-white placeholder:text-zinc-600 outline-none"
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-3">
          <ToggleChk label="Es Promo" checked={form.esPromo} onChange={(v) => setForm({ ...form, esPromo: v })} testid="pf-promo" />
          <ToggleChk label="Es Combo" checked={form.esCombo} onChange={(v) => setForm({ ...form, esCombo: v })} testid="pf-combo" />
          <ToggleChk label="Sugerir en checkout" checked={form.esSugerido} onChange={(v) => setForm({ ...form, esSugerido: v })} testid="pf-sugerido" />
          <ToggleChk label="Sin stock (oculto en tienda)" checked={form.sinStock} onChange={(v) => setForm({ ...form, sinStock: v })} testid="pf-sinstock" />
          <ToggleChk label="Activo (visible)" checked={form.activo} onChange={(v) => setForm({ ...form, activo: v })} testid="pf-activo" />
        </div>

        <div className="mt-4 max-w-xs">
          <FormField label="Etiqueta (badge opcional en la tarjeta)" testid="pf-etiqueta" value={form.etiqueta} onChange={(v) => setForm({ ...form, etiqueta: v })} placeholder="Ej: Rasta, Nueva, 2x1" />
        </div>
        <div className="mt-3 max-w-[140px]">
          <FormField label="Orden en el menú (menor = primero)" testid="pf-orden" type="number" value={form.orden} onChange={(v) => setForm({ ...form, orden: v === "" ? 0 : Number(v) })} />
        </div>

        <div className="mt-6 flex gap-2">
          <button onClick={onClose} className="flex-1 bg-panel border border-line hover:border-white text-white font-bold uppercase text-sm py-3 rounded transition-colors">Cancelar</button>
          <button
            data-testid="pf-save-button"
            onClick={save} disabled={saving || !form.nombre}
            className="flex-1 bg-brand hover:bg-brand-strong disabled:bg-zinc-700 text-black font-black uppercase text-sm py-3 rounded transition-colors"
          >
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, type = "text", as = "input", testid, wrap, autoComplete }) {
  const Comp = as;
  return (
    <label className={`block ${wrap ? "sm:col-span-2" : ""}`}>
      <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">{label}</span>
      <Comp
        data-testid={testid}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={as === "textarea" ? 2 : undefined}
        autoComplete={autoComplete}
        className="mt-1 w-full bg-black border border-line focus:border-brand rounded px-3 py-2 text-sm text-white outline-none"
      />
    </label>
  );
}

function FormSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">{label}</span>
      <select
        data-testid={testid}
        value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-black border border-line focus:border-brand rounded px-3 py-2 text-sm text-white outline-none"
      >
        {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
    </label>
  );
}

function ToggleChk({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input data-testid={testid} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-brand" />
      <span className="text-xs uppercase tracking-widest text-zinc-300 font-bold">{label}</span>
    </label>
  );
}

// ============ PROMOS ============
const DIAS_FULL = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function PromosManager({ authHeader }) {
  const [rules, setRules] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [testing, setTesting] = useState(false);
  const [sugCfg, setSugCfg] = useState({ maxSugerencias: 1, minimoSugerenciaDelivery: "" });
  const [sugSaved, setSugSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => setSugCfg({
      maxSugerencias: r.data?.maxSugerencias ?? 1,
      minimoSugerenciaDelivery: r.data?.minimoSugerenciaDelivery ?? "",
    })).catch(() => {});
  }, []);

  const saveSugCfg = async () => {
    try {
      await axios.post(`${API}/admin/config`, {
        maxSugerencias: Number(sugCfg.maxSugerencias) || 1,
        minimoSugerenciaDelivery: sugCfg.minimoSugerenciaDelivery === "" ? null : Number(sugCfg.minimoSugerenciaDelivery),
      }, { headers: authHeader() });
      setSugSaved(true);
      setTimeout(() => setSugSaved(false), 2000);
      toast.success("Configuración de sugerencias guardada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/promos`, { headers: authHeader() }),
        axios.get(`${API}/admin/products`, { headers: authHeader() }),
      ]);
      setRules(r1.data);
      setProducts(r2.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("¿Eliminar promo?")) return;
    await axios.delete(`${API}/admin/promos/${id}`, { headers: authHeader() });
    toast.success("Promo eliminada");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-3xl uppercase text-white">Promos & Reglas</h2>
          <p className="text-sm text-zinc-500">{rules.length} reglas — se aplican solas cuando el carrito califica</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="admin-probar-promo-button"
            onClick={() => setTesting(true)}
            className="flex items-center gap-2 bg-gold hover:bg-gold-strong text-black font-black uppercase text-xs px-4 py-2 rounded"
          >
            <Flame className="w-4 h-4" strokeWidth={3} /> Probar
          </button>
          <button
            data-testid="admin-add-promo-button"
            onClick={() => setEditing("new")}
            className="flex items-center gap-2 bg-brand hover:bg-brand-strong text-black font-black uppercase text-xs px-4 py-2 rounded"
          >
            <Plus className="w-4 h-4" strokeWidth={3} /> Nueva promo
          </button>
        </div>
      </div>

      <div data-testid="promos-sug-config" className="mb-4 flex flex-wrap items-end gap-3 bg-panel border border-line rounded-lg p-3">
        <div>
          <span className="block text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Sugerencias en el carrito (máx.)</span>
          <input data-testid="sug-max" type="number" min="0" max="10" value={sugCfg.maxSugerencias} onChange={(e) => setSugCfg({ ...sugCfg, maxSugerencias: e.target.value })} className="w-24 bg-black border border-line rounded px-2 py-1.5 text-sm text-white outline-none" />
        </div>
        <div>
          <span className="block text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Mínimo del combo para sugerir en delivery (vacío = sin mínimo)</span>
          <input data-testid="sug-minimo" type="number" min="0" placeholder="Ej: 15000" value={sugCfg.minimoSugerenciaDelivery} onChange={(e) => setSugCfg({ ...sugCfg, minimoSugerenciaDelivery: e.target.value })} className="w-44 bg-black border border-line rounded px-2 py-1.5 text-sm text-white placeholder:text-zinc-600 outline-none" />
        </div>
        <button data-testid="sug-save" onClick={saveSugCfg} className="bg-brand hover:bg-brand-strong text-black font-black uppercase text-[11px] px-3 py-2 rounded transition-colors">
          {sugSaved ? "Guardado ✓" : "Guardar"}
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando...</div>
      ) : rules.length === 0 ? (
        <div className="text-zinc-500 text-sm">No hay promos configuradas.</div>
      ) : (
        <div data-testid="admin-promos-list" className="space-y-2">
          {rules.map((r) => (
            <div key={r.id} data-testid={`admin-promo-row-${r.id}`} className="bg-panel border border-line rounded-lg p-4 flex flex-wrap justify-between gap-3">
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading text-lg text-white">{r.nombre}</span>
                  {!r.activa && <span className="bg-zinc-700 text-zinc-300 text-[9px] px-1.5 py-0.5 rounded font-black uppercase">Inactiva</span>}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">{r.descripcion}</div>
                <div className="text-[11px] text-zinc-400 mt-1">
                  {r.items.map((i, idx) => (
                    <span key={idx} className="inline-block bg-black border border-line rounded px-1.5 py-0.5 mr-1 mb-1">
                      {i.cantidad}× {i.productoId ? (products.find((p) => p.id === i.productoId)?.nombre || i.productoId) : (CATS.find((c) => c.key === i.categoria)?.label || i.categoria)}{i.tamano ? ` (${i.tamano})` : ""}
                    </span>
                  ))}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-gold font-bold mt-1">
                  {r.diasSemana.length === 7 ? "Todos los días" : r.diasSemana.map((d) => DIAS_FULL[d]).join(" · ")}
                  {(r.horaDesde && r.horaHasta) ? ` · ${r.horaDesde}-${r.horaHasta}` : ""}
                  {r.canal && r.canal !== "ambos" ? ` · ${r.canal === "retiro" ? "Solo retiro" : "Solo delivery"}` : ""}
                  {r.vigenciaDesde || r.vigenciaHasta ? ` · vigencia ${r.vigenciaDesde || "…"} → ${r.vigenciaHasta || "…"}` : ""}
                </div>
                <div className="flex gap-1 mt-1">
                  {r.destacada && <span className="bg-gold text-black text-[9px] px-1.5 py-0.5 rounded font-black uppercase">Destacada</span>}
                  {r.sugerible === false && <span className="bg-zinc-700 text-zinc-300 text-[9px] px-1.5 py-0.5 rounded font-black uppercase">No sugerible</span>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="font-heading text-2xl text-gold">{r.descuentoPorcentaje ? `-${r.descuentoPorcentaje}%` : formatARS(r.precioCombo)}</div>
                <button data-testid={`admin-promo-edit-${r.id}`} onClick={() => setEditing(r)} className="w-8 h-8 flex items-center justify-center bg-black border border-line hover:border-white rounded text-zinc-300 hover:text-white transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                <button data-testid={`admin-promo-delete-${r.id}`} onClick={() => del(r.id)} className="w-8 h-8 flex items-center justify-center bg-black border border-line hover:border-red-500 rounded text-zinc-300 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <PromoForm authHeader={authHeader} products={products} rule={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {testing && <PromoTester authHeader={authHeader} products={products} onClose={() => setTesting(false)} />}
    </div>
  );
}

// ============ PROMO TESTER ============
function PromoTester({ authHeader, products, onClose }) {
  const [lines, setLines] = useState([{ productoId: "", tamano: "", cantidad: 1 }]);
  const [extrasCatalogo, setExtrasCatalogo] = useState([]);
  const [lineExtras, setLineExtras] = useState({});
  const [dia, setDia] = useState(new Date().getDay() === 0 ? 6 : new Date().getDay() - 1);
  const [hora, setHora] = useState("");
  const [canal, setCanal] = useState("ambos");
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/extras`, { headers: authHeader() }).then((r) => setExtrasCatalogo(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLine = (idx, patch) => setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const toggleExtra = (idx, key) => setLineExtras((m) => {
    const cur = m[idx] || [];
    return { ...m, [idx]: cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key] };
  });

  const probar = async () => {
    setRunning(true); setError(""); setResult(null);
    try {
      const items = lines.filter((l) => l.productoId).map((l, i) => ({
        productoId: l.productoId,
        tamano: l.tamano || null,
        cantidad: Number(l.cantidad) || 1,
        personalizacion: (lineExtras[i] || []).length ? { tamano: l.tamano || null, extras: lineExtras[i] } : null,
      }));
      const { data } = await axios.post(`${API}/admin/promos/probar`, {
        items, canal, dia: Number(dia), hora: hora || null,
      }, { headers: authHeader() });
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Error al probar");
    } finally { setRunning(false); }
  };

  const esPizza = (pid) => ["piedra", "molde", "xl"].includes(products.find((p) => p.id === pid)?.categoria);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="promo-tester" className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto thin-scroll bg-[#0F0F11] border border-line rounded-xl p-6 slide-up">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-2xl uppercase text-white">Probador de promos</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-2">
          {lines.map((l, idx) => (
            <div key={idx} className="bg-black border border-line rounded-lg p-2">
              <div className="flex flex-wrap items-center gap-2">
                <select data-testid={`pt-producto-${idx}`} value={l.productoId} onChange={(e) => setLine(idx, { productoId: e.target.value })} className="flex-1 min-w-[180px] bg-panel border border-line rounded px-2 py-1.5 text-xs text-white outline-none">
                  <option value="">Elegí producto...</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.nombre}{p.sinStock ? " (sin stock)" : ""}</option>)}
                </select>
                <select data-testid={`pt-tamano-${idx}`} value={l.tamano} onChange={(e) => setLine(idx, { tamano: e.target.value })} className="bg-panel border border-line rounded px-2 py-1.5 text-xs text-white outline-none">
                  <option value="">Sin tamaño</option>
                  <option value="grande">Grande</option>
                  <option value="chica">Chica</option>
                </select>
                <input data-testid={`pt-cantidad-${idx}`} type="number" min="1" value={l.cantidad} onChange={(e) => setLine(idx, { cantidad: e.target.value })} className="w-16 bg-panel border border-line rounded px-2 py-1.5 text-xs text-white outline-none" />
                <button onClick={() => setLines((ls) => ls.filter((_, i) => i !== idx))} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
              {l.productoId && esPizza(l.productoId) && extrasCatalogo.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {extrasCatalogo.filter((e) => e.activo !== false).map((e) => {
                    const on = (lineExtras[idx] || []).includes(e.id);
                    return (
                      <button key={e.id} type="button" data-testid={`pt-extra-${idx}-${e.id}`} onClick={() => toggleExtra(idx, e.id)} className={`text-[10px] px-2 py-1 rounded border font-bold transition-colors ${on ? "bg-gold text-black border-gold" : "bg-panel text-zinc-400 border-line hover:text-white"}`}>
                        {e.label} +{formatARS(e.precio)}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
        <button data-testid="pt-add-line" onClick={() => setLines((ls) => [...ls, { productoId: "", tamano: "", cantidad: 1 }])} className="mt-2 flex items-center gap-1 text-[11px] uppercase tracking-widest font-bold text-gold hover:text-white transition-colors">
          <Plus className="w-3.5 h-3.5" /> Agregar línea
        </button>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <span className="block text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Día</span>
            <select data-testid="pt-dia" value={dia} onChange={(e) => setDia(e.target.value)} className="bg-black border border-line rounded px-2 py-1.5 text-xs text-white outline-none">
              {DIAS_FULL.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
          </div>
          <div>
            <span className="block text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Hora (vacío = ahora)</span>
            <input data-testid="pt-hora" type="time" value={hora} onChange={(e) => setHora(e.target.value)} className="bg-black border border-line rounded px-2 py-1.5 text-xs text-white outline-none" />
          </div>
          <div>
            <span className="block text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Canal</span>
            <div className="flex gap-1.5">
              {[["ambos", "Ambos"], ["delivery", "Delivery"], ["retiro", "Retiro"]].map(([k, lbl]) => (
                <button key={k} type="button" data-testid={`pt-canal-${k}`} onClick={() => setCanal(k)} className={`px-2 py-1.5 rounded text-[10px] font-black uppercase tracking-wider border transition-colors ${canal === k ? "bg-brand text-black border-brand" : "bg-black text-zinc-400 border-line hover:text-white"}`}>{lbl}</button>
              ))}
            </div>
          </div>
          <button data-testid="pt-run" onClick={probar} disabled={running || !lines.some((l) => l.productoId)} className="ml-auto bg-brand hover:bg-brand-strong disabled:bg-zinc-700 text-black font-black uppercase text-xs px-4 py-2 rounded transition-colors">
            {running ? "Probando..." : "Probar"}
          </button>
        </div>

        {error && <div data-testid="pt-error" className="mt-4 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded p-2">{error}</div>}

        {result && (
          <div data-testid="pt-result" className="mt-5 space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-black border border-line rounded p-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Total</div>
                <div data-testid="pt-total" className="font-heading text-2xl text-gold">{formatARS(result.total)}</div>
              </div>
              <div className="bg-black border border-line rounded p-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Sin promo</div>
                <div data-testid="pt-total-sin" className="font-heading text-2xl text-white">{formatARS(result.totalSinPromo)}</div>
              </div>
              <div className="bg-black border border-line rounded p-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Ahorro</div>
                <div data-testid="pt-ahorro" className="font-heading text-2xl text-emerald-400">{formatARS(result.ahorro)}</div>
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Promos que aplican</div>
              {result.promosAplicadas?.length ? result.promosAplicadas.map((a) => (
                <div key={a.promoId} data-testid={`pt-aplicada-${a.promoId}`} className="flex justify-between bg-emerald-500/10 border border-emerald-500/30 rounded px-3 py-2 text-sm mb-1">
                  <span className="text-white font-bold">{a.nombre}{a.veces > 1 ? ` x${a.veces}` : ""}</span>
                  <span className="text-emerald-400 font-bold">-{formatARS(a.ahorro)}</span>
                </div>
              )) : <div data-testid="pt-sin-aplicadas" className="text-xs text-zinc-500">Ninguna promo aplica con estos datos.</div>}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Sugerencias</div>
              {result.sugerencias?.length ? result.sugerencias.map((s) => (
                <div key={s.promoId} data-testid={`pt-sugerencia-${s.promoId}`} className="bg-gold/5 border border-gold/40 rounded px-3 py-2 text-sm mb-1">
                  <span className="text-white font-bold">{s.nombre}</span>
                  <span className="text-zinc-400 text-xs ml-2">{s.textoFaltantes}</span>
                  <span className="text-gold text-xs ml-2">ahorrás {formatARS(s.ahorroEstimado)}</span>
                </div>
              )) : <div data-testid="pt-sin-sugerencias" className="text-xs text-zinc-500">Sin sugerencias.</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const EMPTY_REQ = { tipo: "categoria", categoria: "empanadas", productoId: "", tamano: "", cantidad: 1, sabores: [] };

function PromoForm({ authHeader, products, rule, onClose, onSaved }) {
  const [form, setForm] = useState({
    nombre: rule?.nombre || "",
    descripcion: rule?.descripcion || "",
    tipo: rule?.descuentoPorcentaje ? "porcentaje" : "combo",
    precioCombo: rule?.precioCombo || "",
    descuentoPorcentaje: rule?.descuentoPorcentaje || "",
    prioridad: rule?.prioridad ?? 0,
    diasSemana: rule?.diasSemana || [0, 1, 2, 3, 4, 5, 6],
    horaDesde: rule?.horaDesde || "",
    horaHasta: rule?.horaHasta || "",
    canal: rule?.canal || "ambos",
    vigenciaDesde: rule?.vigenciaDesde || "",
    vigenciaHasta: rule?.vigenciaHasta || "",
    sugerible: rule?.sugerible !== false,
    destacada: rule?.destacada === true,
    activa: rule?.activa !== false,
    items: rule?.items?.map((i) => ({
      tipo: i.productoId ? "producto" : "categoria",
      categoria: i.categoria || "empanadas",
      productoId: i.productoId || "",
      tamano: i.tamano || "",
      cantidad: i.cantidad || 1,
      sabores: i.sabores || [],
    })) || [{ ...EMPTY_REQ }],
  });
  const [saving, setSaving] = useState(false);

  const toggleDia = (d) => setForm((s) => ({ ...s, diasSemana: s.diasSemana.includes(d) ? s.diasSemana.filter((x) => x !== d) : [...s.diasSemana, d].sort((a, b) => a - b) }));
  const setItem = (idx, patch) => setForm((s) => ({ ...s, items: s.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  const addItemRow = () => setForm((s) => ({ ...s, items: [...s.items, { ...EMPTY_REQ }] }));
  const removeItemRow = (idx) => setForm((s) => ({ ...s, items: s.items.filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre,
        descripcion: form.descripcion,
        precioCombo: form.tipo === "combo" ? Number(form.precioCombo) : null,
        descuentoPorcentaje: form.tipo === "porcentaje" ? Number(form.descuentoPorcentaje) : null,
        prioridad: Number(form.prioridad) || 0,
        diasSemana: form.diasSemana,
        horaDesde: form.horaDesde || null,
        horaHasta: form.horaHasta || null,
        canal: form.canal,
        vigenciaDesde: form.vigenciaDesde || null,
        vigenciaHasta: form.vigenciaHasta || null,
        sugerible: form.sugerible,
        destacada: form.destacada,
        activa: form.activa,
        items: form.items.map((i) => ({
          productoId: i.tipo === "producto" && i.productoId ? i.productoId : null,
          categoria: i.tipo === "categoria" ? i.categoria : null,
          tamano: i.tamano || null,
          cantidad: Number(i.cantidad) || 1,
          sabores: i.tipo === "categoria" ? (i.sabores || []) : [],
        })),
      };
      if (rule) {
        await axios.put(`${API}/admin/promos/${rule.id}`, payload, { headers: authHeader() });
        toast.success("Promo actualizada");
      } else {
        await axios.post(`${API}/admin/promos`, payload, { headers: authHeader() });
        toast.success("Promo creada");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const canSave = form.nombre && (form.tipo === "combo" ? form.precioCombo : form.descuentoPorcentaje) && form.diasSemana.length > 0 && form.items.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="admin-promo-form" className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto thin-scroll bg-[#0F0F11] border border-line rounded-xl p-6 slide-up">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-2xl uppercase text-white">{rule ? "Editar promo" : "Nueva promo"}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <FormField label="Nombre" testid="pr-nombre" value={form.nombre} onChange={(v) => setForm({ ...form, nombre: v })} />
          <div>
            <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Tipo de promo</span>
            <div className="mt-1 flex gap-1.5">
              <button
                type="button"
                data-testid="pr-tipo-combo"
                onClick={() => setForm({ ...form, tipo: "combo" })}
                className={`flex-1 px-3 py-2 rounded text-[11px] font-black uppercase tracking-wider border transition-colors ${form.tipo === "combo" ? "bg-brand text-black border-brand" : "bg-black text-zinc-400 border-line hover:text-white"}`}
              >
                Precio combo
              </button>
              <button
                type="button"
                data-testid="pr-tipo-porcentaje"
                onClick={() => setForm({ ...form, tipo: "porcentaje" })}
                className={`flex-1 px-3 py-2 rounded text-[11px] font-black uppercase tracking-wider border transition-colors ${form.tipo === "porcentaje" ? "bg-brand text-black border-brand" : "bg-black text-zinc-400 border-line hover:text-white"}`}
              >
                % Descuento
              </button>
            </div>
          </div>
          {form.tipo === "combo" ? (
            <FormField label="Precio combo" testid="pr-precio" type="number" value={form.precioCombo} onChange={(v) => setForm({ ...form, precioCombo: v })} />
          ) : (
            <FormField label="Descuento %" testid="pr-porcentaje" type="number" value={form.descuentoPorcentaje} onChange={(v) => setForm({ ...form, descuentoPorcentaje: v })} />
          )}
          <FormField label="Descripción" testid="pr-desc" value={form.descripcion} onChange={(v) => setForm({ ...form, descripcion: v })} as="textarea" wrap />
        </div>

        <div className="mt-4">
          <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Días que aplica</span>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {DIAS_FULL.map((d, i) => (
              <button
                key={d}
                data-testid={`pr-dia-${i}`}
                onClick={() => toggleDia(i)}
                className={`px-3 py-1.5 rounded text-[11px] font-black uppercase tracking-wider border transition-colors ${form.diasSemana.includes(i) ? "bg-brand text-black border-brand" : "bg-black text-zinc-400 border-line hover:text-white"}`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Qué incluye el combo</span>
            <button data-testid="pr-add-item" onClick={addItemRow} className="flex items-center gap-1 text-[11px] uppercase tracking-widest font-bold text-gold hover:text-white transition-colors">
              <Plus className="w-3.5 h-3.5" /> Agregar ítem
            </button>
          </div>
          <div className="mt-2 space-y-2">
            {form.items.map((it, idx) => (
              <div key={idx} className="flex flex-wrap items-center gap-2 bg-black border border-line rounded-lg p-2">
                <select data-testid={`pr-item-tipo-${idx}`} value={it.tipo} onChange={(e) => setItem(idx, { tipo: e.target.value })} className="bg-panel border border-line rounded px-2 py-1.5 text-xs text-white outline-none">
                  <option value="categoria">Categoría</option>
                  <option value="producto">Producto</option>
                </select>
                {it.tipo === "categoria" ? (
                  <select data-testid={`pr-item-categoria-${idx}`} value={it.categoria} onChange={(e) => setItem(idx, { categoria: e.target.value })} className="bg-panel border border-line rounded px-2 py-1.5 text-xs text-white outline-none">
                    {CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                ) : (
                  <select data-testid={`pr-item-producto-${idx}`} value={it.productoId} onChange={(e) => setItem(idx, { productoId: e.target.value })} className="flex-1 min-w-[140px] bg-panel border border-line rounded px-2 py-1.5 text-xs text-white outline-none">
                    <option value="">Elegí producto...</option>
                    {products.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                )}
                <select data-testid={`pr-item-tamano-${idx}`} value={it.tamano} onChange={(e) => setItem(idx, { tamano: e.target.value })} className="bg-panel border border-line rounded px-2 py-1.5 text-xs text-white outline-none">
                  <option value="">Cualquier tamaño</option>
                  <option value="grande">Grande</option>
                  <option value="chica">Chica</option>
                </select>
                <input data-testid={`pr-item-cantidad-${idx}`} type="number" min="1" value={it.cantidad} onChange={(e) => setItem(idx, { cantidad: e.target.value })} className="w-16 bg-panel border border-line rounded px-2 py-1.5 text-xs text-white outline-none" />
                <button onClick={() => removeItemRow(idx)} className="text-zinc-500 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                {it.tipo === "categoria" && (
                  <div className="w-full mt-1">
                    <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Sabores que aplican (vacío = todos)</div>
                    <div className="flex flex-wrap gap-1">
                      {products.filter((p) => p.categoria === it.categoria).map((p) => {
                        const on = (it.sabores || []).includes(p.id);
                        return (
                          <button
                            key={p.id}
                            type="button"
                            data-testid={`pr-item-sabor-${idx}-${p.id}`}
                            onClick={() => setItem(idx, { sabores: on ? (it.sabores || []).filter((x) => x !== p.id) : [...(it.sabores || []), p.id] })}
                            className={`text-[10px] px-2 py-1 rounded border font-bold transition-colors ${on ? "bg-brand text-black border-brand" : "bg-panel text-zinc-400 border-line hover:text-white"}`}
                          >
                            {p.nombre}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div>
            <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Franja horaria (opcional)</span>
            <div className="mt-1 flex gap-2">
              <input data-testid="pr-hora-desde" type="time" value={form.horaDesde} onChange={(e) => setForm({ ...form, horaDesde: e.target.value })} className="flex-1 bg-black border border-line focus:border-brand rounded px-3 py-2 text-sm text-white outline-none" />
              <input data-testid="pr-hora-hasta" type="time" value={form.horaHasta} onChange={(e) => setForm({ ...form, horaHasta: e.target.value })} className="flex-1 bg-black border border-line focus:border-brand rounded px-3 py-2 text-sm text-white outline-none" />
            </div>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Canal</span>
            <div className="mt-1 flex gap-1.5">
              {[["ambos", "Ambos"], ["delivery", "Delivery"], ["retiro", "Retiro"]].map(([k, lbl]) => (
                <button key={k} type="button" data-testid={`pr-canal-${k}`} onClick={() => setForm({ ...form, canal: k })} className={`flex-1 px-2 py-2 rounded text-[11px] font-black uppercase tracking-wider border transition-colors ${form.canal === k ? "bg-brand text-black border-brand" : "bg-black text-zinc-400 border-line hover:text-white"}`}>{lbl}</button>
              ))}
            </div>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Vigencia (opcional)</span>
            <div className="mt-1 flex gap-2">
              <input data-testid="pr-vigencia-desde" type="date" value={form.vigenciaDesde} onChange={(e) => setForm({ ...form, vigenciaDesde: e.target.value })} className="flex-1 bg-black border border-line focus:border-brand rounded px-3 py-2 text-sm text-white outline-none" />
              <input data-testid="pr-vigencia-hasta" type="date" value={form.vigenciaHasta} onChange={(e) => setForm({ ...form, vigenciaHasta: e.target.value })} className="flex-1 bg-black border border-line focus:border-brand rounded px-3 py-2 text-sm text-white outline-none" />
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-3">
          <ToggleChk label="Promo activa" checked={form.activa} onChange={(v) => setForm({ ...form, activa: v })} testid="pr-activa" />
          <ToggleChk label="Sugerible en el carrito" checked={form.sugerible} onChange={(v) => setForm({ ...form, sugerible: v })} testid="pr-sugerible" />
          <ToggleChk label="Destacada (portada)" checked={form.destacada} onChange={(v) => setForm({ ...form, destacada: v })} testid="pr-destacada" />
        </div>

        <div className="mt-6 flex gap-2">
          <button onClick={onClose} className="flex-1 bg-panel border border-line hover:border-white text-white font-bold uppercase text-sm py-3 rounded transition-colors">Cancelar</button>
          <button data-testid="pr-save" onClick={save} disabled={saving || !canSave} className="flex-1 bg-brand hover:bg-brand-strong disabled:bg-zinc-700 text-black font-black uppercase text-sm py-3 rounded transition-colors">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ EXTRAS ============
function ExtrasManager({ authHeader }) {
  const [extras, setExtras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/extras`, { headers: authHeader() });
      setExtras(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("¿Eliminar extra?")) return;
    await axios.delete(`${API}/admin/extras/${id}`, { headers: authHeader() });
    toast.success("Extra eliminado");
    load();
  };

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-3xl uppercase text-white">Extras de pizzas</h2>
          <p className="text-sm text-zinc-500">Ingredientes extra que se ofrecen al personalizar una pizza</p>
        </div>
        <button
          data-testid="admin-add-extra-button"
          onClick={() => setEditing("new")}
          className="flex items-center gap-2 bg-brand hover:bg-brand-strong text-black font-black uppercase text-xs px-4 py-2 rounded"
        >
          <Plus className="w-4 h-4" strokeWidth={3} /> Nuevo extra
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando...</div>
      ) : extras.length === 0 ? (
        <div className="text-zinc-500 text-sm">No hay extras configurados.</div>
      ) : (
        <div data-testid="admin-extras-list" className="space-y-2">
          {extras.map((e) => (
            <div key={e.id} data-testid={`admin-extra-row-${e.id}`} className="bg-panel border border-line rounded-lg p-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="font-bold text-white text-sm">{e.label}</span>
                {!e.activo && <span className="bg-zinc-700 text-zinc-300 text-[9px] px-1.5 py-0.5 rounded font-black uppercase">Inactivo</span>}
              </div>
              <div className="flex items-center gap-3">
                <span className="font-heading text-xl text-gold">+{formatARS(e.precio)}</span>
                <button data-testid={`admin-extra-edit-${e.id}`} onClick={() => setEditing(e)} className="w-8 h-8 flex items-center justify-center bg-black border border-line hover:border-white rounded text-zinc-300 hover:text-white transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                <button data-testid={`admin-extra-delete-${e.id}`} onClick={() => del(e.id)} className="w-8 h-8 flex items-center justify-center bg-black border border-line hover:border-red-500 rounded text-zinc-300 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <ExtraForm authHeader={authHeader} extra={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ExtraForm({ authHeader, extra, onClose, onSaved }) {
  const [form, setForm] = useState({
    label: extra?.label || "",
    precio: extra?.precio ?? "",
    activo: extra?.activo !== false,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { label: form.label, precio: Number(form.precio), activo: form.activo };
      if (extra) {
        await axios.put(`${API}/admin/extras/${extra.id}`, payload, { headers: authHeader() });
        toast.success("Extra actualizado");
      } else {
        await axios.post(`${API}/admin/extras`, payload, { headers: authHeader() });
        toast.success("Extra creado");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="admin-extra-form" className="relative w-full max-w-md bg-[#0F0F11] border border-line rounded-xl p-6 slide-up">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-2xl uppercase text-white">{extra ? "Editar extra" : "Nuevo extra"}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <FormField label="Nombre" testid="ex-label" value={form.label} onChange={(v) => setForm({ ...form, label: v })} />
          <FormField label="Precio adicional" testid="ex-precio" type="number" value={form.precio} onChange={(v) => setForm({ ...form, precio: v })} />
          <ToggleChk label="Activo (se ofrece)" checked={form.activo} onChange={(v) => setForm({ ...form, activo: v })} testid="ex-activo" />
        </div>
        <div className="mt-6 flex gap-2">
          <button onClick={onClose} className="flex-1 bg-panel border border-line hover:border-white text-white font-bold uppercase text-sm py-3 rounded transition-colors">Cancelar</button>
          <button data-testid="ex-save" onClick={save} disabled={saving || !form.label || form.precio === ""} className="flex-1 bg-brand hover:bg-brand-strong disabled:bg-zinc-700 text-black font-black uppercase text-sm py-3 rounded transition-colors">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ PAYMENT METHODS ============
function PaymentMethodsManager({ authHeader }) {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/payment-methods`, { headers: authHeader() });
      setMethods(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("¿Eliminar medio de pago?")) return;
    await axios.delete(`${API}/admin/payment-methods/${id}`, { headers: authHeader() });
    toast.success("Medio de pago eliminado");
    load();
  };

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-3xl uppercase text-white">Medios de pago</h2>
          <p className="text-sm text-zinc-500">Los que el cliente puede elegir en el checkout</p>
        </div>
        <button
          data-testid="admin-add-payment-button"
          onClick={() => setEditing("new")}
          className="flex items-center gap-2 bg-brand hover:bg-brand-strong text-black font-black uppercase text-xs px-4 py-2 rounded"
        >
          <Plus className="w-4 h-4" strokeWidth={3} /> Nuevo medio
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando...</div>
      ) : methods.length === 0 ? (
        <div className="text-zinc-500 text-sm">No hay medios de pago configurados.</div>
      ) : (
        <div data-testid="admin-payments-list" className="space-y-2">
          {methods.map((m) => (
            <div key={m.id} data-testid={`admin-payment-row-${m.id}`} className="bg-panel border border-line rounded-lg p-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="font-bold text-white text-sm truncate">{m.nombre}</span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded font-black uppercase shrink-0 ${m.tipo === "efectivo" ? "bg-[#10B981]/20 text-[#10B981]" : m.tipo === "en_local" ? "bg-gold/20 text-gold" : "bg-[#009EE3]/20 text-[#009EE3]"}`}>{m.tipo === "en_local" ? "en el local" : m.tipo}</span>
                {(m.entregas || ["delivery", "retiro"]).map((e) => (
                  <span key={e} className="text-[9px] px-1.5 py-0.5 rounded font-black uppercase shrink-0 bg-line text-zinc-400">{e}</span>
                ))}
                {!m.activo && <span className="bg-zinc-700 text-zinc-300 text-[9px] px-1.5 py-0.5 rounded font-black uppercase shrink-0">Inactivo</span>}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {m.alias && <span className="font-heading text-sm text-gold hidden sm:block">{m.alias}</span>}
                <button data-testid={`admin-payment-edit-${m.id}`} onClick={() => setEditing(m)} className="w-8 h-8 flex items-center justify-center bg-black border border-line hover:border-white rounded text-zinc-300 hover:text-white transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                <button data-testid={`admin-payment-delete-${m.id}`} onClick={() => del(m.id)} className="w-8 h-8 flex items-center justify-center bg-black border border-line hover:border-red-500 rounded text-zinc-300 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <PaymentMethodForm authHeader={authHeader} method={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function PaymentMethodForm({ authHeader, method, onClose, onSaved }) {
  const [form, setForm] = useState({
    nombre: method?.nombre || "",
    tipo: method?.tipo || "transferencia",
    alias: method?.alias || "",
    cbu: method?.cbu || "",
    titular: method?.titular || "",
    instrucciones: method?.instrucciones || "",
    activo: method?.activo !== false,
    entregas: method?.entregas || ["delivery", "retiro"],
  });
  const [saving, setSaving] = useState(false);

  const toggleEntrega = (key) => {
    setForm((f) => ({
      ...f,
      entregas: f.entregas.includes(key) ? f.entregas.filter((e) => e !== key) : [...f.entregas, key],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      if (method) {
        await axios.put(`${API}/admin/payment-methods/${method.id}`, form, { headers: authHeader() });
        toast.success("Medio de pago actualizado");
      } else {
        await axios.post(`${API}/admin/payment-methods`, form, { headers: authHeader() });
        toast.success("Medio de pago creado");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="admin-payment-form" className="relative w-full max-w-md bg-[#0F0F11] border border-line rounded-xl p-6 slide-up max-h-[90vh] overflow-y-auto thin-scroll">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-2xl uppercase text-white">{method ? "Editar medio de pago" : "Nuevo medio de pago"}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <FormField label="Nombre" testid="pm-nombre" value={form.nombre} onChange={(v) => setForm({ ...form, nombre: v })} />
          <div>
            <span className="block text-[11px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Tipo</span>
            <div className="flex gap-2">
              {[["transferencia", "Transferencia"], ["efectivo", "Efectivo"], ["en_local", "En el local"]].map(([t, lbl]) => (
                <button
                  key={t}
                  data-testid={`pm-tipo-${t}`}
                  onClick={() => setForm({ ...form, tipo: t, entregas: t === "en_local" ? ["retiro"] : form.entregas })}
                  className={`flex-1 py-2 rounded border font-bold uppercase text-[11px] tracking-widest transition-colors ${form.tipo === t ? "border-brand bg-brand/10 text-white" : "border-line text-zinc-500 hover:border-white/30"}`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          {form.tipo === "transferencia" && (
            <>
              <FormField label="Alias" testid="pm-alias" value={form.alias} onChange={(v) => setForm({ ...form, alias: v })} />
              <FormField label="CBU / CVU" testid="pm-cbu" value={form.cbu} onChange={(v) => setForm({ ...form, cbu: v })} />
              <FormField label="Titular" testid="pm-titular" value={form.titular} onChange={(v) => setForm({ ...form, titular: v })} />
            </>
          )}
          <FormField label="Instrucciones para el cliente" testid="pm-instrucciones" value={form.instrucciones} onChange={(v) => setForm({ ...form, instrucciones: v })} />
          <div>
            <span className="block text-[11px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Disponible para</span>
            <div className="flex gap-2">
              {[["delivery", "Delivery"], ["retiro", "Retiro en el local"]].map(([key, label]) => (
                <button
                  key={key}
                  data-testid={`pm-entrega-${key}`}
                  onClick={() => toggleEntrega(key)}
                  className={`flex-1 py-2 rounded border font-bold uppercase text-[11px] tracking-widest transition-colors ${form.entregas.includes(key) ? "border-brand bg-brand/10 text-white" : "border-line text-zinc-500 hover:border-white/30"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <ToggleChk label="Activo (se ofrece en el checkout)" checked={form.activo} onChange={(v) => setForm({ ...form, activo: v })} testid="pm-activo" />
        </div>
        <div className="mt-6 flex gap-2">
          <button onClick={onClose} className="flex-1 bg-panel border border-line hover:border-white text-white font-bold uppercase text-sm py-3 rounded transition-colors">Cancelar</button>
          <button data-testid="pm-save" onClick={save} disabled={saving || !form.nombre || form.entregas.length === 0} className="flex-1 bg-brand hover:bg-brand-strong disabled:bg-zinc-700 text-black font-black uppercase text-sm py-3 rounded transition-colors">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ ENVÍOS ============
function EnviosPanel({ authHeader }) {
  const [locales, setLocales] = useState([]);
  const [localId, setLocalId] = useState("");
  const [loading, setLoading] = useState(true);
  const [dirForm, setDirForm] = useState({ direccion: "", mostrar: "", lat: null, lng: null });
  const [zonas, setZonas] = useState([]);
  const [editZona, setEditZona] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mensajes, setMensajes] = useState({ mensajeFueraDeZona: "", mensajeEnvioCorto: "", textoRetiro: "" });

  const local = locales.find((l) => l.id === localId);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/locales`, { headers: authHeader() });
      setLocales(data);
      const activo = data.find((l) => l.activo !== false) || data[0];
      if (activo) {
        setLocalId(activo.id);
        setDirForm({ direccion: activo.direccion || "", mostrar: activo.direccionMostrar || "", lat: activo.lat ?? null, lng: activo.lng ?? null });
        setZonas(activo.zonas || []);
      }
      const cfg = await axios.get(`${API}/config`);
      setMensajes({
        mensajeFueraDeZona: cfg.data.mensajeFueraDeZona || "",
        mensajeEnvioCorto: cfg.data.mensajeEnvioCorto || "",
        textoRetiro: cfg.data.textoRetiro || "",
      });
    } catch { toast.error("Error al cargar envíos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const elegirLocal = (id) => {
    setLocalId(id);
    const l = locales.find((x) => x.id === id);
    if (l) {
      setDirForm({ direccion: l.direccion || "", mostrar: l.direccionMostrar || "", lat: l.lat ?? null, lng: l.lng ?? null });
      setZonas(l.zonas || []);
    }
  };

  const saveUbicacion = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/locales/${localId}`, {
        direccion: dirForm.direccion,
        direccionMostrar: dirForm.mostrar,
        ...(dirForm.lat != null && dirForm.lng != null ? { lat: dirForm.lat, lng: dirForm.lng } : {}),
      }, { headers: authHeader() });
      toast.success("Ubicación del local guardada");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const saveZonas = async (nuevas, msg) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/locales/${localId}`, { zonas: nuevas }, { headers: authHeader() });
      toast.success(msg || "Zonas guardadas");
      setZonas(nuevas);
      setEditZona(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Error al guardar zonas"); }
    finally { setSaving(false); }
  };

  const saveMensajes = async () => {
    try {
      await axios.post(`${API}/admin/config`, mensajes, { headers: authHeader() });
      toast.success("Mensajes guardados");
    } catch (err) { toast.error(err?.response?.data?.detail || "Error al guardar"); }
  };

  if (loading) return <div className="text-zinc-500"><Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Cargando...</div>;
  if (!local) return <div className="text-zinc-500 text-sm">No hay locales configurados.</div>;

  return (
    <div className="max-w-3xl">
      <h2 className="font-heading text-3xl uppercase text-white mb-1">Envíos por zona</h2>
      <p className="text-sm text-zinc-500 mb-5">El servidor calcula distancia, zona, costo, mínimo y envío gratis. El cliente nunca manda el precio del envío.</p>

      {locales.length > 1 && (
        <div className="mb-4">
          <span className="block text-[11px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Local</span>
          <select data-testid="envios-local-select" value={localId} onChange={(e) => elegirLocal(e.target.value)} className="bg-black border border-line rounded px-3 py-2 text-sm text-white outline-none">
            {locales.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
          </select>
        </div>
      )}

      <div className="bg-panel border border-line rounded-xl p-5 mb-4">
        <h3 className="font-heading text-xl uppercase text-white mb-3">Ubicación del local</h3>
        <span className="block text-[11px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Dirección para calcular la distancia (elegila de la lista para fijar el punto en el mapa)</span>
        <AddressAutocomplete
          testid="envios-direccion-input"
          value={dirForm.direccion}
          onChange={(v) => setDirForm({ ...dirForm, direccion: v, lat: null, lng: null })}
          onSelect={({ direccion, lat, lng }) => setDirForm({ ...dirForm, direccion, lat, lng })}
          placeholder="Calle y número del local"
        />
        {dirForm.lat != null && (
          <div data-testid="envios-coords" className="mt-2 text-[11px] text-emerald-400 font-bold uppercase tracking-widest">
            Ubicación lista · {dirForm.lat.toFixed(5)}, {dirForm.lng.toFixed(5)}
          </div>
        )}
        {dirForm.lat == null && dirForm.direccion && (
          <div className="mt-2 text-[11px] text-amber-400 uppercase tracking-widest">Elegí la dirección de la lista para fijar el punto en el mapa</div>
        )}
        <div className="mt-4">
          <FormField label="Dirección para mostrar (encabezado de la tienda, comanda y pantalla de éxito)" testid="envios-direccion-mostrar" value={dirForm.mostrar} onChange={(v) => setDirForm({ ...dirForm, mostrar: v })} placeholder="Ej: Villa Luro 2 - Av. Escalada 596" />
        </div>
        <button data-testid="envios-save-ubicacion" onClick={saveUbicacion} disabled={saving || (!dirForm.mostrar && (!dirForm.direccion || dirForm.lat == null))} className="mt-3 bg-brand hover:bg-brand-strong disabled:bg-zinc-700 text-black font-black uppercase text-xs px-4 py-2.5 rounded transition-colors">
          {saving ? "Guardando..." : "Guardar ubicación"}
        </button>
      </div>

      <div className="bg-panel border border-line rounded-xl p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-xl uppercase text-white">Zonas de envío</h3>
          <button data-testid="envios-add-zona" onClick={() => setEditZona({ nombre: "", kmDesde: 0, kmHasta: 2, costo: 0, gratisDesde: "", pedidoMinimo: "", reparte: "Local", activa: true })} className="flex items-center gap-1.5 bg-brand hover:bg-brand-strong text-black font-black uppercase text-[10px] px-3 py-2 rounded">
            <Plus className="w-3.5 h-3.5" strokeWidth={3} /> Nueva zona
          </button>
        </div>
        {zonas.length === 0 ? (
          <p data-testid="envios-sin-zonas" className="text-sm text-zinc-500">Sin zonas cargadas el delivery sale sin costo ni restricción de distancia (comportamiento anterior). Cargá zonas para cobrar envío por distancia.</p>
        ) : (
          <div className="space-y-2">
            {zonas.map((z, i) => (
              <div key={i} data-testid={`envios-zona-${i}`} className="flex items-center justify-between gap-3 bg-black border border-line rounded-lg px-3 py-2.5">
                <div className="flex-1 min-w-0">
                  <span className="font-bold text-white text-sm">{z.nombre}</span>
                  <span className="text-xs text-zinc-500 ml-2">{z.kmDesde}–{z.kmHasta} km · {z.costo > 0 ? `$${z.costo.toLocaleString("es-AR")}` : "gratis"} · reparte {z.reparte}</span>
                  {z.gratisDesde ? <span className="text-[10px] text-emerald-400 ml-2 uppercase font-bold">gratis desde ${Number(z.gratisDesde).toLocaleString("es-AR")}</span> : null}
                  {z.pedidoMinimo ? <span className="text-[10px] text-amber-400 ml-2 uppercase font-bold">mínimo ${Number(z.pedidoMinimo).toLocaleString("es-AR")}</span> : null}
                  {!z.activa && <span className="text-[10px] text-zinc-500 ml-2 uppercase font-bold">inactiva</span>}
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button data-testid={`envios-zona-edit-${i}`} onClick={() => setEditZona({ ...z, _idx: i })} className="w-8 h-8 flex items-center justify-center bg-panel border border-line hover:border-white rounded text-zinc-300 hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
                  <button data-testid={`envios-zona-del-${i}`} onClick={() => saveZonas(zonas.filter((_, x) => x !== i), "Zona eliminada")} className="w-8 h-8 flex items-center justify-center bg-panel border border-line hover:border-red-500 rounded text-zinc-300 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-panel border border-line rounded-xl p-5">
        <h3 className="font-heading text-xl uppercase text-white mb-3">Mensajes de la tienda</h3>
        <div className="space-y-3">
          <FormField label="Fuera de zona (se muestra cuando no hay reparto a esa dirección)" testid="envios-msg-fuera" value={mensajes.mensajeFueraDeZona} onChange={(v) => setMensajes({ ...mensajes, mensajeFueraDeZona: v })} />
          <FormField label="Mensaje corto de envío en el carrito (ej: Envío gratis a 10 cuadras)" testid="envios-msg-corto" value={mensajes.mensajeEnvioCorto} onChange={(v) => setMensajes({ ...mensajes, mensajeEnvioCorto: v })} />
          <FormField label="Texto de retiro (ej: Listo en 20 minutos, sin fila)" testid="envios-msg-retiro" value={mensajes.textoRetiro} onChange={(v) => setMensajes({ ...mensajes, textoRetiro: v })} />
          <button data-testid="envios-save-mensajes" onClick={saveMensajes} className="bg-brand hover:bg-brand-strong text-black font-black uppercase text-xs px-4 py-2.5 rounded transition-colors">Guardar mensajes</button>
        </div>
      </div>

      {editZona && (
        <ZonaForm
          zona={editZona}
          saving={saving}
          onClose={() => setEditZona(null)}
          onSave={(z) => {
            const nuevas = z._idx != null ? zonas.map((x, i) => (i === z._idx ? z : x)) : [...zonas, z];
            saveZonas(nuevas.map(({ _idx, ...rest }) => rest), "Zona guardada");
          }}
        />
      )}
    </div>
  );
}

function ZonaForm({ zona, saving, onClose, onSave }) {
  const [f, setF] = useState(zona);
  const num = (v) => (v === "" ? "" : Number(v));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="zona-form" className="relative w-full max-w-md bg-[#0F0F11] border border-line rounded-xl p-6 slide-up max-h-[90vh] overflow-y-auto thin-scroll">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-2xl uppercase text-white">{f._idx != null ? "Editar zona" : "Nueva zona"}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <FormField label="Nombre (ej: Cerca, Zona 2)" testid="zf-nombre" value={f.nombre} onChange={(v) => setF({ ...f, nombre: v })} />
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Desde (km)" testid="zf-km-desde" type="number" value={f.kmDesde} onChange={(v) => setF({ ...f, kmDesde: num(v) })} />
            <FormField label="Hasta (km)" testid="zf-km-hasta" type="number" value={f.kmHasta} onChange={(v) => setF({ ...f, kmHasta: num(v) })} />
            <FormField label="Costo de envío ($)" testid="zf-costo" type="number" value={f.costo} onChange={(v) => setF({ ...f, costo: num(v) })} />
            <FormField label="Gratis desde ($, vacío = nunca)" testid="zf-gratis" type="number" value={f.gratisDesde} onChange={(v) => setF({ ...f, gratisDesde: num(v) })} />
          </div>
          <FormField label="Pedido mínimo ($, vacío = sin mínimo)" testid="zf-minimo" type="number" value={f.pedidoMinimo} onChange={(v) => setF({ ...f, pedidoMinimo: num(v) })} />
          <FormField label="Quién reparte (Local, Rapiboy...)" testid="zf-reparte" value={f.reparte} onChange={(v) => setF({ ...f, reparte: v })} />
          <ToggleChk label="Zona activa" checked={f.activa !== false} onChange={(v) => setF({ ...f, activa: v })} testid="zf-activa" />
        </div>
        <div className="mt-6 flex gap-2">
          <button onClick={onClose} className="flex-1 bg-panel border border-line hover:border-white text-white font-bold uppercase text-sm py-3 rounded transition-colors">Cancelar</button>
          <button data-testid="zf-save" onClick={() => onSave(f)} disabled={saving || !f.nombre || f.kmHasta <= f.kmDesde} className="flex-1 bg-brand hover:bg-brand-strong disabled:bg-zinc-700 text-black font-black uppercase text-sm py-3 rounded transition-colors">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ SETTINGS ============
const DIAS_SEMANA = [["0", "Lunes"], ["1", "Martes"], ["2", "Miércoles"], ["3", "Jueves"], ["4", "Viernes"], ["5", "Sábado"], ["6", "Domingo"]];
const HORARIOS_DEFAULT = Object.fromEntries(DIAS_SEMANA.map(([k]) => [k, { activo: true, desde: "18:00", hasta: "23:59" }]));

const CAT_LABELS = { piedra: "Pizzas Piedra", molde: "Pizzas Molde", xl: "Pizzas XL", empanadas: "Empanadas", bebidas: "Bebidas", dulces: "Dulces" };

function SettingsPanel({ authHeader }) {
  const [cfg, setCfg] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [nombre, setNombre] = useState("");
  const [tituloPestana, setTituloPestana] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [mensajes, setMensajes] = useState({ mensajePrincipal: "", mensajeCarrito: "", ogDescripcion: "", ogImagen: "" });
  const [ordenCats, setOrdenCats] = useState(["piedra", "molde", "xl", "empanadas", "bebidas", "dulces"]);
  const [intro, setIntro] = useState(INTRO_DEFAULTS);
  const [horarios, setHorarios] = useState(null);
  const [branding, setBranding] = useState(BRAND_DEFAULTS);
  const fileRef = useRef();
  const { me } = useAuth();
  const tenantSlug = localStorage.getItem("bp_admin_tenant") || me?.memberships?.[0]?.tenant_id || "";

  const mergeIntro = (i) => ({
    ...INTRO_DEFAULTS, ...(i || {}),
    pizzas: { ...INTRO_DEFAULTS.pizzas, ...(i?.pizzas || {}) },
    empanadas: { ...INTRO_DEFAULTS.empanadas, ...(i?.empanadas || {}) },
    piedra: { ...INTRO_DEFAULTS.piedra, ...(i?.piedra || {}) },
    molde: { ...INTRO_DEFAULTS.molde, ...(i?.molde || {}) },
  });

  const load = async () => {
    const { data } = await axios.get(`${API}/config`);
    setCfg(data);
    setNombre(data.nombreLocal || "");
    setTituloPestana(data.tituloPestana || "");
    setWhatsapp(data.whatsappNumber || "");
    setMensajes({
      mensajePrincipal: data.mensajePrincipal || "",
      mensajeCarrito: data.mensajeCarrito || "",
      ogDescripcion: data.ogDescripcion || "",
      ogImagen: data.ogImagen || "",
    });
    if (data.ordenCategorias?.length) setOrdenCats(data.ordenCategorias);
    setIntro(mergeIntro(data.intro));
    setHorarios(data.horarios || HORARIOS_DEFAULT);
    setBranding({ ...BRAND_DEFAULTS, ...(data.branding || {}) });
  };
  useEffect(() => { load(); }, []);

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      await axios.post(`${API}/admin/config`, { logoUrl: data.url, nombreLocal: nombre }, { headers: authHeader() });
      toast.success("Logo actualizado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setUploading(false); }
  };

  const saveAll = async () => {
    await axios.post(`${API}/admin/config`, { nombreLocal: nombre, tituloPestana, logoUrl: cfg?.logoUrl, intro, whatsappNumber: whatsapp, horarios, ...mensajes, ordenCategorias: ordenCats }, { headers: authHeader() });
    toast.success("Configuración guardada");
    load();
  };

  const saveBranding = async () => {
    try {
      await axios.post(`${API}/admin/config`, { branding }, { headers: authHeader() });
      toast.success("Tema guardado — ya se ve en la tienda");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar el tema");
    }
  };

  const uploadCardImage = async (cardKey, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      const newIntro = { ...intro, [cardKey]: { ...intro[cardKey], imagen: data.url } };
      setIntro(newIntro);
      await axios.post(`${API}/admin/config`, { intro: newIntro }, { headers: authHeader() });
      toast.success("Foto actualizada — ya se ve en la tienda");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir");
    } finally { setUploading(false); }
  };

  if (!cfg) return <div className="text-zinc-500"><Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Cargando...</div>;

  return (
    <div className="max-w-2xl">
      <h2 className="font-heading text-3xl uppercase text-white mb-4">Logo & Marca</h2>
      <div className="bg-panel border border-line rounded-xl p-5 space-y-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold mb-2">Logo actual</div>
          <div className="w-24 h-24 rounded bg-black border border-line overflow-hidden flex items-center justify-center">
            {cfg.logoUrl ? <img src={resolveImg(cfg.logoUrl)} alt="logo" className="w-full h-full object-contain" /> : <span className="text-zinc-600 text-xs">Sin logo</span>}
          </div>
          <button
            data-testid="admin-logo-upload"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="mt-3 flex items-center gap-2 bg-brand hover:bg-brand-strong text-black font-black uppercase text-xs px-3 py-2 rounded"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Cambiar logo
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
        </div>
        <div>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Nombre del local</span>
            <input
              data-testid="admin-nombre-local"
              value={nombre} onChange={(e) => setNombre(e.target.value)}
              className="mt-1 w-full bg-black border border-line focus:border-brand rounded px-3 py-2 text-sm text-white outline-none"
            />
          </label>
          <label className="block mt-3">
            <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Título de la pestaña (Google / navegador)</span>
            <input
              data-testid="admin-titulo-pestana"
              value={tituloPestana} onChange={(e) => setTituloPestana(e.target.value)}
              placeholder="Ej: MI PIZZERÍA - CENTRO"
              className="mt-1 w-full bg-black border border-line focus:border-brand rounded px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none"
            />
          </label>
          <label className="block mt-3">
            <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">WhatsApp del local (ej: 5491155555555)</span>
            <input
              data-testid="admin-whatsapp"
              value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)}
              placeholder="5491155555555"
              className="mt-1 w-full bg-black border border-line focus:border-[#25D366] rounded px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none"
            />
          </label>
          <p className="text-[10px] text-zinc-600 mt-1.5">Con número cargado, aparece el botón flotante de WhatsApp en la tienda y el contacto en la confirmación del pedido.</p>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <FormField label="Mensaje principal (franja arriba de la portada, vacío = no se muestra)" testid="cfg-mensaje-principal" value={mensajes.mensajePrincipal} onChange={(v) => setMensajes({ ...mensajes, mensajePrincipal: v })} />
            <FormField label="Mensaje del carrito (debajo del total, vacío = no se muestra)" testid="cfg-mensaje-carrito" value={mensajes.mensajeCarrito} onChange={(v) => setMensajes({ ...mensajes, mensajeCarrito: v })} />
            <FormField label="Descripción para compartir el link (Open Graph)" testid="cfg-og-desc" value={mensajes.ogDescripcion} onChange={(v) => setMensajes({ ...mensajes, ogDescripcion: v })} />
            <FormField label="Imagen para compartir el link (URL, vacío = logo)" testid="cfg-og-imagen" value={mensajes.ogImagen} onChange={(v) => setMensajes({ ...mensajes, ogImagen: v })} />
          </div>
          <div className="mt-4">
            <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Orden de las categorías en la tienda</span>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {ordenCats.map((c, i) => (
                <span key={c} className="flex items-center gap-1 bg-black border border-line rounded px-2 py-1 text-[11px] uppercase font-bold text-zinc-300">
                  {CAT_LABELS[c] || c}
                  <button data-testid={`cfg-cat-up-${c}`} onClick={() => { if (i === 0) return; const n = [...ordenCats]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; setOrdenCats(n); }} className="text-zinc-500 hover:text-gold font-black">↑</button>
                  <button data-testid={`cfg-cat-down-${c}`} onClick={() => { if (i === ordenCats.length - 1) return; const n = [...ordenCats]; [n[i + 1], n[i]] = [n[i], n[i + 1]]; setOrdenCats(n); }} className="text-zinc-500 hover:text-gold font-black">↓</button>
                </span>
              ))}
            </div>
          </div>
          <button
            data-testid="admin-save-nombre"
            onClick={saveAll}
            className="mt-3 bg-gold hover:bg-gold-strong text-black font-black uppercase text-xs px-3 py-2 rounded"
          >Guardar</button>
        </div>
      </div>

      <h3 className="font-heading text-2xl uppercase text-white mt-10 mb-4 flex items-center gap-2"><Palette className="w-5 h-5 text-brand" /> Colores y estilo de la tienda</h3>
      <div className="bg-panel border border-line rounded-xl p-5 space-y-4">
        <p className="text-xs text-zinc-500">Tema visual de tu tienda: se aplica al instante en el catálogo, el carrito y el checkout. La vista previa se actualiza en vivo.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            ["colorPrimario", "Color principal", "branding-color-primario"],
            ["colorAcento", "Color acento", "branding-color-acento"],
            ["colorFondo", "Color de fondo", "branding-color-fondo"],
          ].map(([key, label, testid]) => (
            <label key={key} className="block">
              <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">{label}</span>
              <div className="mt-1 flex items-center gap-2 bg-black border border-line rounded px-2 py-1.5">
                <input
                  data-testid={testid}
                  type="color" value={branding[key]}
                  onChange={(e) => setBranding({ ...branding, [key]: e.target.value.toUpperCase() })}
                  className="w-8 h-8 rounded cursor-pointer bg-transparent border-0 p-0"
                />
                <span className="text-xs text-zinc-400 font-mono">{branding[key]}</span>
              </div>
            </label>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Tipografía de títulos</span>
            <select
              data-testid="branding-fuente"
              value={branding.fuente} onChange={(e) => setBranding({ ...branding, fuente: e.target.value })}
              className="mt-1 w-full bg-black border border-line focus:border-brand rounded px-3 py-2 text-sm text-white outline-none"
            >
              {Object.entries(FUENTES).map(([k, f]) => <option key={k} value={k}>{f.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Estilo de bordes y botones</span>
            <select
              data-testid="branding-radio"
              value={branding.radio} onChange={(e) => setBranding({ ...branding, radio: e.target.value })}
              className="mt-1 w-full bg-black border border-line focus:border-brand rounded px-3 py-2 text-sm text-white outline-none"
            >
              {Object.entries(RADIOS).map(([k, r]) => <option key={k} value={k}>{r.label}</option>)}
            </select>
          </label>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold mb-2">Vista previa en vivo</div>
          <div data-testid="branding-preview" style={brandingVars(branding)} className="rounded-xl border border-line overflow-hidden">
            <div className="bg-ink p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-md bg-brand flex items-center justify-center rotate-[-6deg]">
                    <Flame className="w-4 h-4 text-black" strokeWidth={3} />
                  </div>
                  <span className="font-heading text-xl text-white uppercase">{nombre || "Tu local"}</span>
                </div>
                <span className="bg-brand text-black text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-md">Carrito</span>
              </div>
              <div className="mt-3 bg-panel border border-line rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-widest text-gold font-bold">Promo</div>
                <div className="font-heading text-lg text-white uppercase mt-0.5">Pizza de ejemplo</div>
                <button type="button" className="mt-2 bg-brand text-black text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-md">Agregar</button>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            data-testid="admin-save-branding"
            onClick={saveBranding}
            className="flex-1 bg-brand hover:bg-brand-strong text-black font-black uppercase text-sm py-3 rounded transition-colors"
          >Guardar tema</button>
          {tenantSlug && (
            <a
              data-testid="branding-ver-tienda"
              href={`/?tenant=${tenantSlug}`} target="_blank" rel="noreferrer"
              className="flex items-center justify-center gap-2 bg-black border border-line hover:border-white text-white text-xs font-bold uppercase tracking-widest px-4 py-3 rounded transition-colors"
            ><ExternalLink className="w-3.5 h-3.5" /> Ver tienda con este tema</a>
          )}
        </div>
      </div>

      <h3 className="font-heading text-2xl uppercase text-white mt-10 mb-4">Horarios del local</h3>
      <div className="bg-panel border border-line rounded-xl p-5 space-y-2">
        <p className="text-xs text-zinc-500 mb-3">Fuera de estos horarios la tienda muestra un cartel de "cerrado" y no deja confirmar pedidos.</p>
        {DIAS_SEMANA.map(([num, label]) => {
          const h = (horarios || HORARIOS_DEFAULT)[num] || { activo: true, desde: "18:00", hasta: "23:59" };
          const setH = (patch) => setHorarios((s) => ({ ...(s || HORARIOS_DEFAULT), [num]: { ...h, ...patch } }));
          return (
            <div key={num} className="flex items-center gap-2 sm:gap-3">
              <button
                data-testid={`horario-activo-${num}`}
                onClick={() => setH({ activo: !h.activo })}
                className={`w-24 sm:w-28 shrink-0 py-2 rounded border font-bold uppercase text-[10px] tracking-widest transition-colors ${h.activo ? "border-brand bg-brand/10 text-white" : "border-line text-zinc-600"}`}
              >
                {label}
              </button>
              <input
                data-testid={`horario-desde-${num}`}
                type="time" value={h.desde} disabled={!h.activo} onChange={(e) => setH({ desde: e.target.value })}
                className="flex-1 bg-black border border-line focus:border-brand rounded px-2 py-2 text-sm text-white outline-none disabled:opacity-30"
              />
              <span className="text-zinc-600 text-xs">a</span>
              <input
                data-testid={`horario-hasta-${num}`}
                type="time" value={h.hasta} disabled={!h.activo} onChange={(e) => setH({ hasta: e.target.value })}
                className="flex-1 bg-black border border-line focus:border-brand rounded px-2 py-2 text-sm text-white outline-none disabled:opacity-30"
              />
            </div>
          );
        })}
        <button
          data-testid="admin-save-horarios"
          onClick={saveAll}
          className="w-full bg-brand hover:bg-brand-strong text-black font-black uppercase text-sm py-3 rounded transition-colors !mt-4"
        >Guardar horarios</button>
      </div>

      <h3 className="font-heading text-2xl uppercase text-white mt-10 mb-4">Pantalla de entrada (cuestionario)</h3>
      <div className="bg-panel border border-line rounded-xl p-5 space-y-4">
        <FormField label="Pregunta paso 1" testid="intro-paso1-titulo" value={intro.paso1Titulo} onChange={(v) => setIntro({ ...intro, paso1Titulo: v })} />
        <FormField label="Pregunta paso 2 (pizzas)" testid="intro-paso2-titulo" value={intro.paso2Titulo} onChange={(v) => setIntro({ ...intro, paso2Titulo: v })} />
        {INTRO_CARDS.map((c) => (
          <IntroCardEditor key={c.key} cardKey={c.key} label={c.label} intro={intro} setIntro={setIntro} uploading={uploading} onUpload={uploadCardImage} />
        ))}
        <button
          data-testid="admin-save-intro"
          onClick={saveAll}
          className="w-full bg-brand hover:bg-brand-strong text-black font-black uppercase text-sm py-3 rounded transition-colors"
        >Guardar pantalla de entrada</button>
      </div>
    </div>
  );
}

function IntroCardEditor({ cardKey, label, intro, setIntro, uploading, onUpload }) {
  const fileRef = useRef();
  const card = intro[cardKey];
  const setCard = (patch) => setIntro((s) => ({ ...s, [cardKey]: { ...s[cardKey], ...patch } }));
  return (
    <div className="border border-line rounded-lg p-3 space-y-2">
      <div className="text-xs uppercase tracking-widest text-gold font-bold">{label}</div>
      <div className="flex gap-2">
        <div className="flex-1">
          <FormField label="Título" testid={`intro-${cardKey}-titulo`} value={card.titulo} onChange={(v) => setCard({ titulo: v })} />
        </div>
        <div className="flex-1">
          <FormField label="Subtítulo" testid={`intro-${cardKey}-subtitulo`} value={card.subtitulo} onChange={(v) => setCard({ subtitulo: v })} />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-16 h-16 rounded bg-black border border-line overflow-hidden shrink-0">
          {card.imagen && <img src={resolveImg(card.imagen)} alt="" className="w-full h-full object-cover" />}
        </div>
        <button
          data-testid={`intro-${cardKey}-upload`}
          type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="flex items-center gap-2 bg-black border border-line hover:border-white text-white text-[10px] uppercase tracking-widest font-bold px-3 py-2 rounded"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Subir foto
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(cardKey, e)} />
        <input
          data-testid={`intro-${cardKey}-imagen`}
          type="text" value={card.imagen} onChange={(e) => setCard({ imagen: e.target.value })} placeholder="o pegá una URL"
          className="flex-1 bg-black border border-line focus:border-brand rounded px-3 py-2 text-xs text-white placeholder:text-zinc-600 outline-none"
        />
      </div>
    </div>
  );
}

// ============ ORDERS ============
const ESTADOS_ORDEN = {
  pendiente: { label: "Pendiente", cls: "text-amber-400 border-amber-500/40 bg-amber-500/10" },
  pagado: { label: "Pagado MP", cls: "text-sky-400 border-sky-500/40 bg-sky-500/10" },
  comandado: { label: "Comandado", cls: "text-brand border-brand/50 bg-brand/10" },
  terminado: { label: "Terminado", cls: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" },
  rechazado: { label: "Rechazado", cls: "text-red-400 border-red-500/40 bg-red-500/10" },
};

const pagoLabel = (o) =>
  o.pagoDetalle?.nombre
  || (o.metodoPago === "mercadopago" ? "Mercado Pago (online)"
    : !o.metodoPago || o.metodoPago === "efectivo" ? "Efectivo"
    : o.metodoPago);

const esEfectivo = (o) => (o.pagoDetalle?.tipo || o.metodoPago) === "efectivo";
const esTransferenciaPendiente = (o) => o.pagoDetalle?.tipo === "transferencia" && !o.pagoConfirmado;

const fechaPedido = (o) => {
  const f = o.fechaCreacion ? new Date(o.fechaCreacion) : null;
  return f && !isNaN(f) ? f : null;
};

const fechaPedidoTxt = (o) => {
  const f = fechaPedido(o);
  return f ? `${f.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" })} ${f.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", hourCycle: "h23" })}` : "";
};

const ORIGEN_LABELS = { google: "Google", meta: "Meta", directo: "Directo", otro: "Otro" };
const origenLabel = (o) => ORIGEN_LABELS[o.origen?.fuente || "directo"] || "Directo";

function OrdersPanel({ authHeader }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState({ impresionAutomatica: true, sonidoComanda: true, nombreLocal: "", direccionLocal: "" });
  const [printOrder, setPrintOrder] = useState(null);
  const [filtroFecha, setFiltroFecha] = useState("hoy");
  const [filtroOrigen, setFiltroOrigen] = useState("todos");
  const [mostrarPruebas, setMostrarPruebas] = useState(false);
  const knownRef = useRef(null);
  const cfgRef = useRef(cfg);
  const audioRef = useRef(null);

  const playComandaSound = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!audioRef.current) audioRef.current = new Ctx();
      const ctx = audioRef.current;
      ctx.resume();
      const end = ctx.currentTime + 6;
      for (let t = ctx.currentTime; t < end; t += 0.5) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "square";
        osc.frequency.setValueAtTime(880, t);
        osc.frequency.setValueAtTime(660, t + 0.25);
        gain.gain.setValueAtTime(0.12, t);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(t);
        osc.stop(t + 0.45);
      }
    } catch {}
  };

  const setEstado = async (codigo, estado) => {
    await axios.put(`${API}/admin/orders/${codigo}/estado`, { estado }, { headers: authHeader() });
    setOrders((prev) => prev.map((o) => (o.codigo === codigo ? { ...o, estado } : o)));
  };

  const avisarCliente = (o) => {
    const phone = (o.telefono || "").replace(/\D/g, "");
    if (!phone) { toast.error("El pedido no tiene teléfono del cliente"); return; }
    const esRetiro = (o.direccion || "").toUpperCase().includes("RETIRO");
    const localNombre = cfgRef.current.nombreLocal ? ` de ${cfgRef.current.nombreLocal}` : "";
    const msg = `¡Hola ${o.nombreCliente}! Tu pedido ${o.codigo}${localNombre} ya está listo${esRetiro ? " para retirar por el local" : " y está por salir"}. Total: ${formatARS(o.total)}. ¡Gracias por tu compra!`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const [rechazando, setRechazando] = useState(null); // pedido a confirmar rechazo
  const [motivoRechazo, setMotivoRechazo] = useState("");
  const [notificarRechazo, setNotificarRechazo] = useState(true);

  const MOTIVOS_RECHAZO = [
    "Sin stock de algún producto",
    "Fuera de la zona de reparto",
    "No podemos tomar pedidos ahora",
    "Pedido duplicado o de prueba",
  ];

  const confirmarRechazo = async () => {
    const o = rechazando;
    if (!o) return;
    try {
      await setEstado(o.codigo, "rechazado");
      toast.success(`Pedido ${o.codigo} rechazado`);
      if (notificarRechazo) {
        const phone = (o.telefono || "").replace(/\D/g, "");
        if (phone) {
          const localNombre = cfgRef.current.nombreLocal || "";
          const motivo = motivoRechazo.trim();
          const msg = `¡Hola ${o.nombreCliente}! Te escribimos${localNombre ? ` de ${localNombre}` : ""} por tu pedido ${o.codigo}. Lamentablemente no pudimos tomarlo${motivo ? `: ${motivo}` : ""}. Disculpá las molestias.`;
          window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
        } else {
          toast.warning("El pedido no tiene teléfono del cliente; no se pudo notificar");
        }
      }
    } catch { toast.error("Error al rechazar el pedido"); }
    finally { setRechazando(null); setMotivoRechazo(""); setNotificarRechazo(true); }
  };

  const printComanda = (order) => {
    setPrintOrder(order);
    return new Promise((resolve) => {
      setTimeout(() => {
        window.print();
        setTimeout(() => { setPrintOrder(null); resolve(); }, 900);
      }, 300);
    });
  };

  const handleNewOrder = async (order) => {
    if (cfgRef.current.sonidoComanda) playComandaSound();
    if (!cfgRef.current.impresionAutomatica) {
      toast.success(`Nuevo pedido ${order.codigo}`, { description: "Impresión automática OFF — comandalo manualmente" });
      return;
    }
    await printComanda(order);
    try { await setEstado(order.codigo, "comandado"); } catch {}
    toast.success(`Nuevo pedido ${order.codigo}`, { description: "Comanda impresa automáticamente" });
  };

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/orders`, { headers: authHeader() });
      const nuevos = knownRef.current
        ? data.filter((o) => !knownRef.current.has(o.codigo) && ["pendiente", "pagado"].includes(o.estado))
        : [];
      knownRef.current = new Set(data.map((o) => o.codigo));
      setOrders(data);
      setLoading(false);
      for (const o of nuevos) {
        await handleNewOrder(o);
      }
    } catch {}
  };

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => {
      const c = {
        impresionAutomatica: r.data.impresionAutomatica !== false,
        sonidoComanda: r.data.sonidoComanda !== false,
        nombreLocal: r.data.nombreLocal || "",
        direccionLocal: r.data.direccionLocal || "",
      };
      setCfg(c);
      cfgRef.current = c;
    }).catch(() => {});
    fetchOrders();
    const t = setInterval(fetchOrders, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveCfg = async (patch) => {
    const next = { ...cfgRef.current, ...patch };
    cfgRef.current = next;
    setCfg(next);
    try {
      await axios.post(`${API}/admin/config`, patch, { headers: authHeader() });
      toast.success("Configuración guardada");
    } catch {
      toast.error("No se pudo guardar la configuración");
    }
  };

  const comandarManual = async (order) => {
    if (esTransferenciaPendiente(order)) {
      toast.warning(`Pedido ${order.codigo}: todavía no figura como pagado`, { description: "Se comanda igual, pero confirmá la transferencia antes de entregar" });
    }
    await printComanda(order);
    try {
      await setEstado(order.codigo, "comandado");
      toast.success(`Pedido ${order.codigo} comandado`);
    } catch { toast.error("Error al comandar"); }
  };

  const marcarPago = async (codigo) => {
    try {
      await axios.put(`${API}/admin/orders/${codigo}/pago`, {}, { headers: authHeader() });
      setOrders((prev) => prev.map((o) => (o.codigo === codigo ? { ...o, pagoConfirmado: true } : o)));
      toast.success(`Pedido ${codigo}: pago recibido`);
    } catch { toast.error("Error al marcar el pago"); }
  };

  const archivar = async (codigo) => {
    try {
      await axios.put(`${API}/admin/orders/${codigo}/archivar`, {}, { headers: authHeader() });
      toast.success(`Pedido ${codigo} archivado`);
      fetchOrders();
    } catch {
      toast.error("No se pudo archivar el pedido");
    }
  };

  const archivarPruebas = async () => {
    try {
      const { data } = await axios.put(`${API}/admin/orders/archivar-pruebas`, {}, { headers: authHeader() });
      toast.success(`${data.archivados} pedidos de prueba archivados`);
      fetchOrders();
    } catch {
      toast.error("No se pudieron archivar las pruebas");
    }
  };

  const exportCSV = () => {
    // Los pedidos de prueba nunca se exportan
    const rows = filtrados.filter((o) => !o.esPrueba);
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lineas = [
      ["Fecha", "Número", "Cliente", "Total", "Medio de pago", "Envío", "Zona", "Origen"].map(esc).join(","),
      ...rows.map((o) => [
        fechaPedidoTxt(o),
        o.codigo,
        o.nombreCliente,
        o.total,
        pagoLabel(o),
        o.direccion === "RETIRO EN LOCAL" ? "Retiro en local" : "Delivery",
        o.direccion === "RETIRO EN LOCAL" ? "" : o.direccion,
        origenLabel(o),
      ].map(esc).join(",")),
    ];
    const blob = new Blob(["﻿" + lineas.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `pedidos-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`${rows.length} pedidos exportados a CSV`);
  };

  const filtrados = orders.filter((o) => {
    if (o.archivado) return false;
    if (o.esPrueba && !mostrarPruebas) return false;
    if (filtroOrigen !== "todos" && (o.origen?.fuente || "directo") !== filtroOrigen) return false;
    if (filtroFecha === "todos") return true;
    const f = fechaPedido(o);
    if (!f) return false;
    const ahora = new Date();
    const inicioHoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
    if (filtroFecha === "hoy") return f >= inicioHoy;
    if (filtroFecha === "ayer") return f >= new Date(inicioHoy.getTime() - 86400000) && f < inicioHoy;
    return f >= new Date(inicioHoy.getTime() - 6 * 86400000);
  });

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-heading text-3xl uppercase text-white">Pedidos</h2>
          <p className="text-sm text-zinc-500">Se actualiza solo cada 10 seg · al entrar uno nuevo suena 6 seg y se imprime la comanda</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="orders-test-sound"
            onClick={playComandaSound}
            className="flex items-center gap-2 px-3 py-2 rounded border border-line bg-black text-zinc-300 hover:text-white hover:border-white text-[10px] font-black uppercase tracking-widest transition-colors"
          >
            <Bell className="w-3.5 h-3.5" /> Probar sonido
          </button>
          <CfgToggle label="Impresión automática" value={cfg.impresionAutomatica} onChange={(v) => saveCfg({ impresionAutomatica: v })} testid="orders-autoprint-toggle" icon={<Printer className="w-3.5 h-3.5" />} />
          <CfgToggle label="Sonido" value={cfg.sonidoComanda} onChange={(v) => saveCfg({ sonidoComanda: v })} testid="orders-sound-toggle" icon={<Bell className="w-3.5 h-3.5" />} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        {[["hoy", "Hoy"], ["ayer", "Ayer"], ["7dias", "Últimos 7 días"], ["todos", "Todos"]].map(([k, label]) => (
          <button
            key={k}
            data-testid={`orders-filter-${k}`}
            onClick={() => setFiltroFecha(k)}
            className={`px-3 py-1.5 rounded text-[10px] font-black uppercase tracking-widest transition-colors ${filtroFecha === k ? "bg-brand text-black" : "bg-black border border-line text-zinc-400 hover:text-white hover:border-white"}`}
          >{label}</button>
        ))}
        <span data-testid="orders-filter-count" className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500">{filtrados.length} pedido{filtrados.length === 1 ? "" : "s"}</span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mr-1">Origen:</span>
        {[["todos", "Todos"], ["google", "Google"], ["meta", "Meta"], ["directo", "Directo"]].map(([k, label]) => (
          <button
            key={k}
            data-testid={`orders-origin-${k}`}
            onClick={() => setFiltroOrigen(k)}
            className={`px-3 py-1.5 rounded text-[10px] font-black uppercase tracking-widest transition-colors ${filtroOrigen === k ? "bg-gold text-black" : "bg-black border border-line text-zinc-400 hover:text-white hover:border-white"}`}
          >{label}</button>
        ))}
        <button
          data-testid="orders-toggle-pruebas"
          onClick={() => setMostrarPruebas(!mostrarPruebas)}
          className={`px-3 py-1.5 rounded text-[10px] font-black uppercase tracking-widest transition-colors ${mostrarPruebas ? "bg-amber-400 text-black" : "bg-black border border-line text-zinc-400 hover:text-white hover:border-white"}`}
        >{mostrarPruebas ? "Ocultar pruebas" : "Mostrar pruebas"}</button>
        {orders.some((o) => o.esPrueba && !o.archivado) && (
          <button
            data-testid="orders-archivar-pruebas"
            onClick={archivarPruebas}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-[10px] font-black uppercase tracking-widest bg-black border border-amber-400/60 text-amber-300 hover:bg-amber-400 hover:text-black transition-colors"
          ><Archive className="w-3 h-3" /> Archivar todas las pruebas</button>
        )}
        <button
          data-testid="orders-export-csv"
          onClick={exportCSV}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded text-[10px] font-black uppercase tracking-widest bg-black border border-line text-zinc-300 hover:border-white hover:text-white transition-colors"
        ><Download className="w-3 h-3" /> Exportar CSV</button>
      </div>

      {loading ? (
        <div className="text-zinc-500"><Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Cargando...</div>
      ) : orders.length === 0 ? (
        <div className="text-zinc-500 text-sm">Aún no hay pedidos.</div>
      ) : filtrados.length === 0 ? (
        <div data-testid="admin-orders-empty-filter" className="text-zinc-500 text-sm">No hay pedidos en este período.</div>
      ) : (
        <div data-testid="admin-orders-list" className="space-y-2">
          {filtrados.map((o) => {
            const st = ESTADOS_ORDEN[o.estado] || ESTADOS_ORDEN.pendiente;
            const terminado = o.estado === "terminado";
            const rechazado = o.estado === "rechazado";
            return (
              <div
                key={o.codigo}
                data-testid={`order-row-${o.codigo}`}
                className={`bg-panel border border-line rounded-lg p-4 ${terminado ? "opacity-50" : ""} ${rechazado ? "opacity-60 border-red-500/30" : ""}`}
              >
                <div className="flex flex-wrap justify-between gap-3">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading text-lg text-white">{o.codigo}</span>
                      <span data-testid={`order-estado-${o.codigo}`} className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded border ${st.cls}`}>{st.label}</span>
                      {fechaPedidoTxt(o) && (
                        <span data-testid={`order-fecha-${o.codigo}`} className="text-[10px] text-zinc-500 uppercase tracking-widest">{fechaPedidoTxt(o)}</span>
                      )}
                      <span data-testid={`order-origen-${o.codigo}`} className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded border ${(o.origen?.fuente || "directo") === "google" ? "border-blue-400/50 text-blue-300" : (o.origen?.fuente) === "meta" ? "border-sky-400/50 text-sky-300" : "border-line text-zinc-400"}`}>{origenLabel(o)}</span>
                      {o.esPrueba && (
                        <span data-testid={`order-prueba-${o.codigo}`} className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded border border-amber-400/60 text-amber-300">Prueba</span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {o.nombreCliente} · {o.telefono} · {o.entrega === "retiro" ? "Retiro en el local" : o.direccion}
                    </div>
                    {o.entrega !== "retiro" && o.zona && (
                      <div data-testid={`order-envio-${o.codigo}`} className="text-[11px] text-sky-300 mt-0.5">
                        Envío {o.envio > 0 ? formatARS(o.envio) : "gratis"} · {o.zona} · reparte {o.reparte} · {o.distanciaKm} km
                      </div>
                    )}
                    <div className="text-xs text-zinc-400 mt-1.5 space-y-0.5">
                      {o.items.map((i, idx) => (
                        <div key={idx}>
                          <span className="text-gold font-bold">{i.cantidad}×</span> {i.nombre}
                          {!i.extrasDetalle?.length && i.personalizacion?.extras?.length > 0 && <span className="text-zinc-500"> (+{i.personalizacion.extras.join(", ")})</span>}
                          {i.extrasDetalle?.map((x, xi) => (
                            <div key={xi} className="pl-7 text-zinc-400">+ {x.label} · {formatARS(x.extra)}</div>
                          ))}
                        </div>
                      ))}
                    </div>
                    {o.notas && <div className="text-[11px] text-amber-400/80 mt-1">Notas: {o.notas}</div>}
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                      {pagoLabel(o)}{esEfectivo(o) && o.montoEfectivo ? ` · paga con ${formatARS(o.montoEfectivo)}` : ""}
                      {o.ahorro > 0 && <span className="text-emerald-400"> · promo -{formatARS(o.ahorro)}</span>}
                    </div>
                    {o.pagoDetalle?.tipo === "transferencia" && (
                      <div className="mt-1.5 flex items-center gap-2">
                        {o.pagoConfirmado ? (
                          <span data-testid={`order-pago-ok-${o.codigo}`} className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded border border-emerald-500/40 text-emerald-400 bg-emerald-500/10">Pago recibido</span>
                        ) : (
                          <>
                            <span data-testid={`order-pago-pendiente-${o.codigo}`} className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded border border-amber-400/60 text-amber-300 bg-amber-500/10">Esperando transferencia</span>
                            <button
                              data-testid={`order-pago-recibido-${o.codigo}`}
                              onClick={() => marcarPago(o.codigo)}
                              className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded bg-emerald-600 hover:bg-emerald-500 text-black transition-colors"
                            >
                              <CheckCircle2 className="w-3 h-3" /> Pago recibido
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="font-heading text-2xl text-gold">{formatARS(o.total)}</div>
                    <div className="flex gap-1.5">
                      <button
                        data-testid={`order-archivar-${o.codigo}`}
                        onClick={() => archivar(o.codigo)}
                        title="Archivar (oculta sin borrar)"
                        className="flex items-center gap-1.5 bg-black border border-line hover:border-red-400 text-zinc-500 hover:text-red-300 font-bold uppercase text-[10px] px-3 py-2 rounded transition-colors"
                      >
                        <Archive className="w-3.5 h-3.5" /> Archivar
                      </button>
                      {["pendiente", "pagado"].includes(o.estado) && (
                        <>
                          <button
                            data-testid={`order-comandar-${o.codigo}`}
                            onClick={() => comandarManual(o)}
                            className="flex items-center gap-1.5 bg-brand hover:bg-brand-strong text-black font-black uppercase text-[10px] px-3 py-2 rounded transition-colors"
                          >
                            <Printer className="w-3.5 h-3.5" strokeWidth={3} /> Comandar
                          </button>
                          <button
                            data-testid={`order-rechazar-${o.codigo}`}
                            onClick={() => { setRechazando(o); setMotivoRechazo(""); setNotificarRechazo(true); }}
                            className="flex items-center gap-1.5 bg-red-600/20 hover:bg-red-600/40 border border-red-500/50 text-red-300 hover:text-red-200 font-black uppercase text-[10px] px-3 py-2 rounded transition-colors"
                          >
                            <X className="w-3.5 h-3.5" strokeWidth={3} /> Rechazar
                          </button>
                        </>
                      )}
                      {o.estado === "comandado" && (
                        <>
                          <button
                            data-testid={`order-reimprimir-${o.codigo}`}
                            onClick={() => printComanda(o)}
                            className="flex items-center gap-1.5 bg-black border border-line hover:border-white text-zinc-300 hover:text-white font-bold uppercase text-[10px] px-3 py-2 rounded transition-colors"
                          >
                            <Printer className="w-3.5 h-3.5" /> Reimprimir
                          </button>
                          <button
                            data-testid={`order-terminar-${o.codigo}`}
                            onClick={() => setEstado(o.codigo, "terminado")}
                            className="bg-emerald-600 hover:bg-emerald-500 text-black font-black uppercase text-[10px] px-3 py-2 rounded transition-colors"
                          >
                            Terminar
                          </button>
                        </>
                      )}
                      {terminado && (
                        <>
                          <button
                            data-testid={`order-reimprimir-${o.codigo}`}
                            onClick={() => printComanda(o)}
                            className="flex items-center gap-1.5 bg-black border border-line hover:border-white text-zinc-400 hover:text-white font-bold uppercase text-[10px] px-3 py-2 rounded transition-colors"
                          >
                            <Printer className="w-3.5 h-3.5" /> Reimprimir
                          </button>
                          <button
                            data-testid={`order-avisar-${o.codigo}`}
                            onClick={() => avisarCliente(o)}
                            className="flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1FB457] text-black font-black uppercase text-[10px] px-3 py-2 rounded transition-colors"
                          >
                            <MessageCircle className="w-3.5 h-3.5" strokeWidth={3} /> Avisar
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {rechazando && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setRechazando(null)} />
          <div data-testid="rechazar-modal" className="relative w-full max-w-md bg-[#0F0F11] border border-red-500/40 rounded-xl p-6 slide-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-2xl uppercase text-white">Rechazar pedido {rechazando.codigo}</h3>
              <button onClick={() => setRechazando(null)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-zinc-400 mb-4">{rechazando.nombreCliente} · {formatARS(rechazando.total)}</p>
            <div className="space-y-2 mb-4">
              {MOTIVOS_RECHAZO.map((m) => (
                <button
                  key={m}
                  type="button"
                  data-testid={`rechazo-motivo-${MOTIVOS_RECHAZO.indexOf(m)}`}
                  onClick={() => setMotivoRechazo(m)}
                  className={`w-full text-left px-3 py-2.5 rounded border text-xs font-bold transition-colors ${motivoRechazo === m ? "border-red-500 bg-red-500/10 text-white" : "border-line text-zinc-400 hover:text-white"}`}
                >
                  {m}
                </button>
              ))}
            </div>
            <label className="block mb-4">
              <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">O escribí el motivo</span>
              <textarea
                data-testid="rechazo-motivo-libre"
                value={motivoRechazo}
                onChange={(e) => setMotivoRechazo(e.target.value)}
                rows={2}
                placeholder="Ej: nos quedamos sin cheddar"
                className="mt-1 w-full bg-black border border-line focus:border-red-500 rounded px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none"
              />
            </label>
            <button
              type="button"
              data-testid="rechazo-notificar-toggle"
              onClick={() => setNotificarRechazo((v) => !v)}
              className={`mb-5 w-full flex items-center justify-center gap-2 px-4 py-3 rounded text-xs font-black uppercase tracking-widest border transition-colors ${notificarRechazo ? "bg-[#25D366]/15 border-[#25D366]/60 text-[#25D366]" : "bg-black border-line text-zinc-500"}`}
            >
              <MessageCircle className="w-4 h-4" /> {notificarRechazo ? "Notificar al cliente por WhatsApp" : "No notificar al cliente"}
            </button>
            <div className="flex gap-2">
              <button onClick={() => setRechazando(null)} className="flex-1 bg-panel border border-line hover:border-white text-white font-bold uppercase text-sm py-3 rounded transition-colors">Cancelar</button>
              <button
                data-testid="rechazo-confirmar"
                onClick={confirmarRechazo}
                className="flex-1 bg-red-600 hover:bg-red-500 text-white font-black uppercase text-sm py-3 rounded transition-colors"
              >
                Rechazar pedido
              </button>
            </div>
          </div>
        </div>
      )}

      {printOrder && (
        <div id="comanda-print" data-testid="comanda-print">
          <div style={{ fontFamily: "monospace", padding: 14, maxWidth: 340, lineHeight: 1.3, color: "#000", background: "#fff" }}>
            <div style={{ textAlign: "center", fontWeight: 900, fontSize: 32 }}>{(cfg.nombreLocal || "COMANDA").toUpperCase()}</div>
            {cfg.direccionLocal && <div style={{ textAlign: "center", fontSize: 19 }}>{cfg.direccionLocal}</div>}
            <hr />
            <div style={{ fontWeight: 900, fontSize: 26 }}>PEDIDO {printOrder.codigo}</div>
            <div style={{ fontSize: 17 }}>{new Date(printOrder.fechaCreacion).toLocaleString("es-AR")}</div>
            <hr />
            <div style={{ fontSize: 19 }}>{printOrder.nombreCliente} — {printOrder.telefono}</div>
            <div style={{ fontSize: 19 }}>{printOrder.entrega === "retiro" ? "RETIRO EN EL LOCAL" : printOrder.direccion}</div>
            {printOrder.entrega !== "retiro" && printOrder.zona && (
              <div style={{ fontSize: 17 }}>ENVÍO: {printOrder.zona} · reparte {printOrder.reparte} · {printOrder.distanciaKm} km</div>
            )}
            <hr />
            {printOrder.items.map((i, idx) => (
              <div key={idx} style={{ fontSize: 22, marginBottom: 4 }}>
                <strong>{i.cantidad}x</strong> {i.nombre}
                {(i.extrasDetalle?.length
                  ? i.extrasDetalle.map((x) => `+ ${x.label} $${(x.extra || 0).toLocaleString("es-AR")}`)
                  : (i.personalizacion?.extras || []).map((x) => `+ ${x}`)
                ).map((linea, xi) => (
                  <div key={xi} style={{ fontSize: 17 }}>{linea}</div>
                ))}
              </div>
            ))}
            {printOrder.notas && <div style={{ fontSize: 19, marginTop: 4 }}><strong>NOTAS:</strong> {printOrder.notas}</div>}
            <hr />
            <div style={{ fontSize: 19 }}>
              PAGO: {pagoLabel(printOrder).toUpperCase()}{esEfectivo(printOrder) && printOrder.montoEfectivo ? ` — paga con $${printOrder.montoEfectivo.toLocaleString("es-AR")}` : ""}
              {printOrder.pagoDetalle?.tipo === "transferencia" && (
                <span style={{ fontWeight: 900 }}>{printOrder.pagoConfirmado ? " ✓ RECIBIDA" : " — ESPERANDO TRANSFERENCIA"}</span>
              )}
            </div>
            {printOrder.envio > 0 && <div style={{ fontSize: 19 }}>ENVÍO: ${printOrder.envio.toLocaleString("es-AR")}</div>}
            <div style={{ fontWeight: 900, fontSize: 26 }}>TOTAL: ${printOrder.total.toLocaleString("es-AR")}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function CfgToggle({ label, value, onChange, testid, icon }) {
  return (
    <button
      data-testid={testid}
      onClick={() => onChange(!value)}
      className={`flex items-center gap-2 px-3 py-2 rounded border text-[10px] font-black uppercase tracking-widest transition-colors ${
        value ? "border-emerald-500 text-emerald-400 bg-emerald-500/10" : "border-line text-zinc-500 bg-black"
      }`}
    >
      {icon} {label} {value ? "ON" : "OFF"}
    </button>
  );
}

// ============ PASSWORD ============
function PasswordPanel({ authHeader }) {
  const [actual, setActual] = useState("");
  const [nuevo, setNuevo] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/change-password`, { passwordActual: actual, passwordNuevo: nuevo }, { headers: authHeader() });
      toast.success("Contraseña cambiada");
      setActual(""); setNuevo("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-md">
      <h2 className="font-heading text-3xl uppercase text-white mb-4">Cambiar contraseña</h2>
      <div className="bg-panel border border-line rounded-xl p-5 space-y-3">
        <FormField label="Contraseña actual" testid="pw-actual" value={actual} onChange={setActual} type="password" />
        <FormField label="Nueva contraseña" testid="pw-nueva" value={nuevo} onChange={setNuevo} type="password" />
        <button
          data-testid="pw-save"
          onClick={save} disabled={saving || nuevo.length < 6}
          className="w-full bg-brand hover:bg-brand-strong disabled:bg-zinc-700 text-black font-black uppercase text-sm py-3 rounded transition-colors"
        >
          {saving ? "Guardando..." : "Guardar"}
        </button>
      </div>
    </div>
  );
}
